// import DocumentPicker from "expo-document-picker";
import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { Icon } from "@iconify/react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import {
  setupPortalContainers,
  teardownPortalContainers,
} from "../utils/portalContainers"; // Add toast import
import { useNavigate } from "react-router-dom"; // Add navigation import
import { getUserData } from "./const";
import { getActiveItems } from "../utils/statusUtils";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import "../assets/css/theme-table-header.css";
import {
  listMetalGroups,
  listProductGroups,
  listSuppliers,
  createJangad,
  listProducts,
  listCustomerUsers,
  showProducts,
  showJangad,
  isErpApiSuccess,
} from "../erpClient";
import {
  normalizeProductStockListResponse,
  normalizeProductStockShowResponse,
} from "../utils/productStockErpAdapter";
import {
  createJangadIdempotencyKey,
  isJangadsApiSuccess,
  createVisitorsPayload,
} from "../utils/jangadsErpAdapter";
import { mapCustomerUsersToPartyList } from "../utils/customerUsersErpAdapter";
import { extractListRowsFromResponse } from "../utils/parseApiPagination";


// Add this new component right before the Table component
const InlineAutoCompleteInput = ({
  value,
  onChange,
  onFocus,
  onBlur,
  suggestion,
  onComplete,
  id,
  className,
  style,
  ...props
}) => {
  const displaySuggestion = suggestion ? suggestion.split(" (")[0] : "";
  const [isActive, setIsActive] = useState(false);

  const valStr = String(value ?? "");
  const hasMatchingSuffix =
    Boolean(displaySuggestion) &&
    Boolean(valStr.trim()) &&
    displaySuggestion.toLowerCase().startsWith(valStr.toLowerCase()) &&
    displaySuggestion.length > valStr.length;

  const remainingSuffix = hasMatchingSuffix
    ? displaySuggestion.slice(valStr.length)
    : "";

  return (
    <div className="position-relative" style={{ width: "100%" }}>
      <input
        type="text"
        value={value}
        onChange={onChange}
        onFocus={(e) => {
          setIsActive(true);
          onFocus && onFocus(e);
        }}
        onBlur={(e) => {
          setIsActive(false);
          onBlur && onBlur(e);
        }}
        onKeyDown={(e) => {
          if ((e.key === "Tab" || e.key === "Enter") && hasMatchingSuffix) {
            e.preventDefault();
            onComplete(displaySuggestion);
          }
        }}
        className={className}
        style={style}
        {...props}
      />
      {hasMatchingSuffix && isActive && (
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            right: 0,
            bottom: 0,
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            padding: style?.padding || "0.4rem 0.5rem",
            fontSize: style?.fontSize || "0.85rem",
            fontFamily: "inherit",
            lineHeight: 1.5,
            whiteSpace: "nowrap",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          <span style={{ visibility: "hidden", opacity: 0 }}>{valStr}</span>
          <span style={{ color: "#9ca3af" }}>{remainingSuffix}</span>
        </div>
      )}
    </div>
  );
};

const AddJangad = ({ masterGoldRate, masterSilverRate, editData }) => {
  const { buttonClass, tableClass, cardClass } = useButtonTableStyle();
  const navigate = useNavigate(); // Add navigation hook
  const [lastSearchedTagPrefix, setLastSearchedTagPrefix] = useState("");
  const [tagNoApiCalled, setTagNoApiCalled] = useState(false);
  const jangadCreateIdempotencyKeyRef = useRef(null);
  const jangadCreatePayloadFingerprintRef = useRef(null);

  const calculatePerGramRate = (rate, metalType) => {
    if (metalType === "Gold") return rate / 10;
    if (metalType === "Silver") return rate / 1000;
    return rate;
  };
  // Calculate itrate from purity and live price
  const calculatePurityBasedRate = (purity, baseRate, metalType) => {
    const purityValue = parseFloat(purity);
    if (Number.isNaN(purityValue) || !baseRate) return 0;
    if (metalType === "Gold") {
      return (baseRate / 10) * (purityValue / 24);
    } else if (metalType === "Silver") {
      return (baseRate * purityValue) / 999 / 1000;
    }
    return calculatePerGramRate(baseRate, metalType);
  };

  const COLUMNS = [
    { id: "no", label: "No.", width: "20px", value: (row) => row.no },
    {
      id: "tagNo",
      label: "Tag No",
      width: "75px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.tagNo}
          onChange={(e) => onChange(row.id, "tagNo", e.target.value)}
        />
      ),
    },
    {
      id: "productGroup",
      label: (
        <>
          Product Group <span className="text-danger">*</span>
        </>
      ),
      width: "275px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.productGroup}
          onChange={(e) => onChange(row.id, "productGroup", e.target.value)}
        />
      ),
    },
    {
      id: "pcs",
      label: (
        <>
          Pcs <span className="text-danger">*</span>
        </>
      ),
      width: "70px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.pcs}
          onChange={(e) => onChange(row.id, "pcs", e.target.value)}
        />
      ),
    },
    {
      id: "grWt",
      label: (
        <>
          GWt <span className="text-danger">*</span>
        </>
      ),
      width: "90px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.grWt}
          onChange={(e) => onChange(row.id, "grWt", e.target.value)}
        />
      ),
    },
    {
      id: "lsWt",
      label: "LWt",
      width: "90px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.lsWt}
          onChange={(e) => onChange(row.id, "lsWt", e.target.value)}
        />
      ),
    },
    {
      id: "netWt",
      label: "NWt",
      width: "90px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.netWt}
          onChange={(e) => onChange(row.id, "netWt", e.target.value)}
        />
      ),
    },
    {
      id: "remarks",
      label: "Remarks",
      width: "240px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.remarks || ""}
          onChange={(e) => onChange(row.id, "remarks", e.target.value)}
        />
      ),
    },
  ];

  // Demo data for dropdowns
  const [dropdownData, setDropdownData] = useState({
    productGroup: [],
    tagNo: [],
    design: [],
    supplier: [],
  });

  // Helper function to calculate and update itRate and itAmt for a row based on product group
  const calculateAndUpdateItRate = (tableData, rowIndex) => {
    let newTableData = [...tableData];
    const productGroupCell = newTableData[rowIndex].data.find(
      (cell) => cell.id === "productGroup"
    );

    if (!productGroupCell || !productGroupCell.value) {
      return newTableData;
    }

    // Find the selected product group object to get its purity
    const selectedProductGroup = dropdownData.productGroup.find(
      (pg) =>
        pg.label === productGroupCell.value ||
        pg.short_code === productGroupCell.value
    );

    if (selectedProductGroup) {
      // Determine base rate based on metal type
      const baseRate =
        selectedProductGroup.metal === "Gold"
          ? masterGoldRate
          : masterSilverRate;

      let purityBasedRate = 0;

      // Derive purity from label/short_code when backend purity is missing
      let effectivePurity = selectedProductGroup.purity;
      if (!effectivePurity) {
        const labelStr = String(productGroupCell.value || "").toLowerCase();
        // Try formats like "24 carat", "24k"
        const goldMatch = labelStr.match(/(\d+)\s*(carat|k)\b/);
        if (goldMatch && selectedProductGroup.metal === "Gold") {
          effectivePurity = goldMatch[1]; // interpret as karat value out of 24
        }
        // For silver, try to pick 925/999 numbers if present
        if (!effectivePurity && selectedProductGroup.metal === "Silver") {
          const silverMatch = labelStr.match(/\b(\d{3,4})\b/); // e.g., 925 or 999
          if (silverMatch) effectivePurity = silverMatch[1];
        }
      }

      if (effectivePurity) {
        // Calculate purity-based rate
        purityBasedRate = calculatePurityBasedRate(
          effectivePurity,
          baseRate,
          selectedProductGroup.metal
        );
      } else {
        // If no purity, use per gram rate directly
        purityBasedRate = calculatePerGramRate(
          baseRate,
          selectedProductGroup.metal
        );
      }

      // Find and update the itRate cell
      const itRateCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "itrate"
      );

      if (itRateCell) {
        const isOverridden = newTableData[rowIndex].userOverrodeItRate;
        const effectiveRate = isOverridden
          ? parseFloat(itRateCell.value) || 0
          : purityBasedRate;
        if (!isOverridden) {
          itRateCell.value = purityBasedRate.toFixed(2);
        }

        // Recalculate itAmt after updating/using effective itRate
        const netWtCell = newTableData[rowIndex].data.find(
          (cell) => cell.id === "netWt"
        );

        if (netWtCell) {
          const netWt = parseFloat(netWtCell?.value) || 0;
          const itAmt = (netWt * effectiveRate).toFixed(2);
          const itAmtCell = newTableData[rowIndex].data.find(
            (cell) => cell.id === "itAmt"
          );
          if (itAmtCell) {
            itAmtCell.value = itAmt.toString();
          }

          // Recalculate total amount
          const lbrAmtCell = newTableData[rowIndex].data.find(
            (cell) => cell.id === "lbrAmt"
          );
          const totalAmtCell = newTableData[rowIndex].data.find(
            (cell) => cell.id === "totalamt"
          );

          if (totalAmtCell) {
            const lbrAmt = parseFloat(lbrAmtCell?.value || 0);
            const itAmtVal = parseFloat(itAmt || 0);
            const totalAmt = (lbrAmt + itAmtVal).toFixed(2);
            totalAmtCell.value = totalAmt.toString();
          }
        }
      }
    }

    return newTableData;
  };

  const fetchTagNoByPrefix = async (prefix) => {
    if (!prefix || prefix.length < 3) {
      return;
    }

    if (lastSearchedTagPrefix === prefix) {
      return;
    }

    try {
      const response = await listProducts({
        search: prefix,
      });
      let products = normalizeProductStockListResponse(response);
      if (!products.length) {
        const showRes = await showProducts({ tag_no: prefix });
        products = normalizeProductStockShowResponse(showRes);
      }

      if (products.length > 0) {
        const fetchedTags = products.map((item) => {
          // Handle if item is a string (tag number) or an object
          if (typeof item === "string") {
            return {
              value: item,
              label: item,
              tag_no: item,
            };
          }
          return {
            value: item.id,
            label: item.tag_no,
            ...item, // Keep all other properties
          };
        });

        setDropdownData((prev) => ({
          ...prev,
          tagNo: fetchedTags,
        }));
        setLastSearchedTagPrefix(prefix);
        setTagNoApiCalled(true);
      } else {
        setDropdownData((prev) => ({
          ...prev,
          tagNo: [],
        }));
        setTagNoApiCalled(true);
      }
    } catch (error) {
      console.error("Error fetching tag no options:", error);
      setDropdownData((prev) => ({
        ...prev,
        tagNo: [],
      }));
      setTagNoApiCalled(true);
    }
  };

  const sendTagNoToApi = async (tagNo, selectedRowIndex = null) => {
    try {
      const response = await showProducts({ tag_no: tagNo });
      let products = normalizeProductStockShowResponse(response);

      if (products.length > 0) {
        const productData = products[0]; // Assuming exact match returns one item
        const nestedProduct =
          (productData && typeof productData === "object" && productData.product) ||
          (productData && typeof productData === "object" && productData.data) ||
          null;
        const pick = (...keys) => {
          for (const k of keys) {
            if (
              productData &&
              Object.prototype.hasOwnProperty.call(productData, k) &&
              productData[k] !== undefined &&
              productData[k] !== null &&
              productData[k] !== ""
            ) {
              return productData[k];
            }
            if (
              nestedProduct &&
              Object.prototype.hasOwnProperty.call(nestedProduct, k) &&
              nestedProduct[k] !== undefined &&
              nestedProduct[k] !== null &&
              nestedProduct[k] !== ""
            ) {
              return nestedProduct[k];
            }
          }
          return "";
        };
        // Prefer explicit row index from dropdown selection; fallback to matching by tag text
        const rowIndex =
          selectedRowIndex != null
            ? selectedRowIndex
            : tableData.findIndex((row) =>
              row.data.some((cell) => {
                if (cell.id !== "tagNo") return false;
                return (
                  String(cell.value || "").trim().toLowerCase() ===
                  String(tagNo || "").trim().toLowerCase()
                );
              })
            );

        if (rowIndex !== -1) {
          const newTableData = [...tableData];
          const rowData = newTableData[rowIndex].data;

          // Helper to update cell value
          const updateCell = (id, value) => {
            const cell = rowData.find((c) => c.id === id);
            if (cell) cell.value = value === null || value === undefined ? "" : String(value);
          };

          // Map API response to columns
          // Need to find product group label from ID
          const productGroupId = pick("product_group_id", "product_group");
          let productGroupLabel =
            pick("product_group_name", "product_type") ??
            productGroupId ??
            "";
          if (dropdownData.productGroup.length > 0) {
            const pg = dropdownData.productGroup.find(
              (p) => String(p.value) === String(productGroupId)
            );
            if (pg) productGroupLabel = pg.label;
          }

          updateCell("productGroup", productGroupLabel);
          const productGroupCell = rowData.find((c) => c.id === "productGroup");
          if (
            productGroupCell &&
            productGroupId !== undefined &&
            productGroupId !== null &&
            productGroupId !== ""
          ) {
            productGroupCell.productGroupId = Number(productGroupId) || productGroupId;
          }
          updateCell("pcs", pick("pcs"));
          updateCell("grWt", pick("gwt"));
          updateCell("lsWt", pick("lwt_customer"));
          updateCell("netWt", pick("nwt"));

          // Trigger rate calculation
          setTableData(calculateAndUpdateItRate(newTableData, rowIndex));
        }
      }
    } catch (error) {
      console.error("Error in API call:", error);
    }
  };

  // Function to fetch product group options by prefix (when user types 3+ characters)
  // Also fetches all product groups when no prefix is provided (on mount)
  const fetchProductGroupByPrefix = async (prefix = "") => {
    // Avoid duplicate API calls for the same prefix
    if (
      prefix &&
      prefix.length >= 3 &&
      lastSearchedProductGroupPrefix === prefix
    ) {
      return;
    }

    try {
      const response = await listProductGroups();
      if (response?.success && response.data) {
        const metalGroupsResponse = await listMetalGroups();

        // Create a map of metal group IDs to their purity
        const metalGroupsMap = {};
        const mgList =
          metalGroupsResponse?.success &&
            Array.isArray(metalGroupsResponse.data?.metal_groups)
            ? metalGroupsResponse.data.metal_groups
            : metalGroupsResponse?.success &&
              Array.isArray(metalGroupsResponse.data?.records)
              ? metalGroupsResponse.data.records
              : [];
        mgList.forEach((group) => {
          metalGroupsMap[group.id] = {
            purity: group.purity || "",
            metal: group.metal || "",
          };
        });

        const pgList = Array.isArray(response.data.product_groups)
          ? response.data.product_groups
          : [];
        const filteredPgList =
          prefix && prefix.length >= 3
            ? pgList.filter((g) =>
              String(g.name || "")
                .toLowerCase()
                .includes(String(prefix).toLowerCase()),
            )
            : pgList;
        // New API doesn't guarantee status; treat all as active for dropdown.
        const activeProductGroups = filteredPgList;

        const fetchedProductGroups = activeProductGroups.map((group) => {
          // Extract metal type from product group label
          let metalType = "";
          const label = String(group.name || "").toLowerCase();
          if (label.includes("gold")) {
            metalType = "Gold";
          } else if (label.includes("silver")) {
            metalType = "Silver";
          } else if (label.includes("platinum")) {
            metalType = "Platinum";
          } else if (metalGroupsMap[group.metal_group_id]?.metal) {
            metalType = metalGroupsMap[group.metal_group_id].metal;
          }

          return {
            value: group.id,
            label: group.name || "",
            short_code: group.short_code || "",
            purity: metalGroupsMap[group.metal_group_id]?.purity || "",
            metal: metalType,
          };
        });

        // Remove duplicates
        const uniqueProductGroups = Array.from(
          new Map(
            fetchedProductGroups.map((item) => [item.value, item])
          ).values()
        );

        // Update dropdown data with fetched product group options
        setDropdownData((prev) => ({
          ...prev,
          productGroup: uniqueProductGroups,
        }));

        if (prefix && prefix.length >= 3) {
          setLastSearchedProductGroupPrefix(prefix);
          setProductGroupApiCalled(true); // Mark that API was called
        }
      } else {
        // If no product groups found or error message
        setDropdownData((prev) => ({
          ...prev,
          productGroup: [],
        }));
        if (prefix && prefix.length >= 3) {
          setProductGroupApiCalled(true); // Mark that API was called (even if no results)
        }
      }
    } catch (error) {
      console.error("Error fetching product group options:", error);
      // On error, clear the dropdown
      setDropdownData((prev) => ({
        ...prev,
        productGroup: [],
      }));
      if (prefix && prefix.length >= 3) {
        setProductGroupApiCalled(true); // Mark that API was called (even on error)
      }
    }
  };

  // Listen for API URL changes and clear cached dropdowns.
  // Data is lazily fetched on user interaction.
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log('API URL changed in AddJangad, clearing cached dropdowns...', event.detail);
      setDropdownData((prev) => ({
        ...prev,
        productGroup: [],
      }));
      setLastSearchedProductGroupPrefix("");
      setProductGroupApiCalled(false);
    };

    window.addEventListener('apiUrlChanged', handleApiUrlChange);

    return () => {
      window.removeEventListener('apiUrlChanged', handleApiUrlChange);
    };
  }, []);

  // Recalculate itrate when live master rates change
  useEffect(() => {
    setTableData((prev) => {
      let updated = [...prev];
      for (let i = 0; i < updated.length; i++) {
        const pgCell = updated[i].data.find((c) => c.id === "productGroup");
        if (pgCell && pgCell.value) {
          updated = calculateAndUpdateItRate(updated, i);
        }
      }
      return updated;
    });
  }, [masterGoldRate, masterSilverRate, dropdownData.productGroup]);

  // (Old fetchCustomers/fetchSuppliers/fetchKarigars removed; data now loaded lazily by prefix)

  // Fetch minimal dropdown options by name prefix (3+ chars)
  const fetchMobileOptionsByPrefix = async (type, prefix) => {
    try {
      if (!prefix || prefix.length < 3) {
        setMobileDropdownOptions([]);
        return;
      }

      let options = [];

      if (type === "Customer") {
        const response = await listCustomerUsers({ search: prefix });
        const fetchedCustomers = mapCustomerUsersToPartyList(response);

        if (fetchedCustomers?.length) {
          options = fetchedCustomers
            .filter((c) => c.fullName || c.name)
            .map((c) => {
              const fullName = c.fullName || c.name || "";
              const mobile = c.mobile || "";
              return {
                value: mobile || fullName,
                label: mobile ? `${fullName} (${mobile})` : fullName,
                name: fullName,
                id: c.id,
                mobile,
                personType: "Customer",
              };
            });
        }
      } else if (type === "Supplier/Karigar") {
        // Fetch only Karigar options
        const response = await listSuppliers({ search: prefix, status: 1 });
        if (isErpApiSuccess(response)) {
          const suppliers = extractListRowsFromResponse(response, [
            "suppliers",
          ]);
          // Filter active karigars (status === 1); allow missing status when API already filtered
          const activeKarigars = suppliers.filter(
            (karigar) =>
              karigar.status === 1 ||
              karigar.status === "1" ||
              karigar.status == null ||
              karigar.status === "",
          );

          options = activeKarigars
            .filter((karigar) => {
              // Include if has company_name or name or short_code
              return karigar.company_name || karigar.name || karigar.short_code;
            })
            .map((karigar) => {
              // Use company_name as primary, fallback to name, then short_code
              const displayName =
                karigar.company_name ||
                karigar.name ||
                karigar.short_code ||
                "";
              const contactNumber = karigar.contact_number || "";

              // Build label: "CompanyName (MobileNumber)" - show company name first, then mobile in parentheses
              let label = displayName;
              if (contactNumber) {
                label = `${displayName} (${contactNumber})`;
              }

              return {
                value: contactNumber || karigar.short_code || displayName, // Use contact_number, or short_code, or displayName as value
                label: label,
                name: displayName, // clean display name
                id: karigar.id,
                personType: "Karigar", // Track person type
                shortCode: karigar.short_code || "", // Store short code
                mobile: contactNumber || "", // Store mobile number separately (empty if no number)
              };
            });
        }
      }

      setMobileDropdownOptions(options);
      setShowMobileDropdown(true);
      setMobileHighlightedIndex(0);
    } catch (error) {
      console.error("Error fetching options by prefix:", error);
      // On error, clear dropdown
      setMobileDropdownOptions([]);
      setShowMobileDropdown(false);
    }
  };

  // Columns that should have dropdown functionality
  const dropdownColumns = ["productGroup", "tagNo"];

  const initialRowData = COLUMNS.map((col) => ({
    id: col.id,
    value: "",
    // Add imageFile property for image column
    ...(col.id === "image" ? { imageFile: null, imagePreview: null } : {}),
  }));

  const [tableData, setTableData] = useState([{ id: 1, data: initialRowData }]);
  const [activeDropdown, setActiveDropdown] = useState({
    row: null,
    col: null,
    type: null,
  });
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [lastSearchedProductGroupPrefix, setLastSearchedProductGroupPrefix] =
    useState("");
  const [productGroupApiCalled, setProductGroupApiCalled] = useState(false); // Track if API was called to show "No matching items"
  const dropdownRef = useRef(null);
  const inputRefs = useRef({});
  const dropdownItemsRef = useRef([]);
  const fileInputRefs = useRef({});
  const tooltipRefs = useRef({}); // Add refs for tooltips

  // Add state to track active tooltip
  const [activeTooltip, setActiveTooltip] = useState(null);
  const tooltipTimeoutRef = useRef(null);

  // State for image modal
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageUrl: null,
    zoomLevel: 1,
  });

  // Other Amount modal state removed as field is not needed

  // Add state to track last Enter keypress time
  const [lastEnterPress, setLastEnterPress] = useState({
    time: 0,
    rowIndex: null,
  });

  // Update dropdown position after API response when dropdown is active
  useEffect(() => {
    if (
      productGroupApiCalled &&
      activeDropdown.type === "productGroup" &&
      activeDropdown.row !== null &&
      activeDropdown.col !== null
    ) {
      setTimeout(() => {
        const inputElement =
          inputRefs.current[`${activeDropdown.row}-${activeDropdown.col}`];
        if (inputElement) {
          const rect = inputElement.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width,
          });
        }
      }, 10);
    }
  }, [productGroupApiCalled, dropdownData.productGroup, activeDropdown]);

  // Update the otherAmountColumnWidths state to use fixed widths
  const [otherAmountColumnWidths, setOtherAmountColumnWidths] = useState({
    no: "10px", // Added for No. column
    chargetype: "150px", // Changed from chargeType to match transformation
    pcs: "20px",
    ct: "50px",
    wt: "60px",
    con: "10px",
    rateon: "50px",
    rate: "68px",
    amount: "80px",
  });

  // Add rateOn options
  const rateOnOptions = ["FIXED", "PCS", "CT", "WT"];

  const [billDate, setBillDate] = useState("");
  const [orderType, setOrderType] = useState("Customer"); // New state for dropdown

  // Visitor fields state
  const [noOfVisitors, setNoOfVisitors] = useState(1);
  const [visitorNames, setVisitorNames] = useState("");
  const [visitorMobiles, setVisitorMobiles] = useState("");
  const [reference, setReference] = useState("");
  const [remarks, setRemarks] = useState("");

  // (Customer, Supplier, Karigar lists are not stored anymore; data is fetched lazily via prefix APIs)
  // Selected mobile and name states
  const [selectedMobile, setSelectedMobile] = useState("");
  const [selectedName, setSelectedName] = useState("");
  const [selectedPersonId, setSelectedPersonId] = useState(null);
  const [selectedPersonType, setSelectedPersonType] = useState(null); // Track if selected person is Supplier or Karigar
  const [showMobileDropdown, setShowMobileDropdown] = useState(false);
  const [mobileDropdownOptions, setMobileDropdownOptions] = useState([]);
  const mobileDropdownRef = useRef(null);
  const mobileInputRef = useRef(null);
  const mobileDropdownItemsRef = useRef([]);
  const [mobileHighlightedIndex, setMobileHighlightedIndex] = useState(0);

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setBillDate(today);
  }, []);

  const handleDateChange = (e) => {
    setBillDate(e.target.value);
  };

  // Prefill when editing (Edit Jangad passes editData)
  useEffect(() => {
    if (!editData) return;
    // Header fields
    const exp = String(editData.exp_delivery || "").split(/[: T]/)[0];
    if (exp) setBillDate(exp);
    if (editData.group_type) {
      const t =
        editData.group_type.charAt(0).toUpperCase() +
        editData.group_type.slice(1).toLowerCase();
      setOrderType(t);
    }
    if (editData.no_of_visitors) setNoOfVisitors(editData.no_of_visitors);
    if (editData.visitor_names) setVisitorNames(editData.visitor_names);
    if (editData.visitor_mobiles) setVisitorMobiles(editData.visitor_mobiles);
    if (editData.reference) setReference(editData.reference);
    if (editData.remarks) setRemarks(editData.remarks);
  }, [editData]);

  // Prefill table rows once product groups are available
  useEffect(() => {
    if (!editData || !Array.isArray(editData.jangad_entries)) return;
    const hasPg =
      Array.isArray(dropdownData.productGroup) &&
      dropdownData.productGroup.length > 0;
    if (!hasPg) return;

    const rows = editData.jangad_entries.map((item, idx) => {
      const pgId = item.product_group;
      const pgObj = dropdownData.productGroup.find((pg) => pg.value === pgId);
      const pgLabel = pgObj ? pgObj.label : item.product_type || "";
      const mapped = COLUMNS.map((col) => {
        switch (col.id) {
          case "no":
            return { id: col.id, value: idx + 1 };
          case "productGroup":
            return { id: col.id, value: pgLabel };
          case "pcs":
            return { id: col.id, value: String(item.pcs || "") };
          case "grWt":
            return {
              id: col.id,
              value: String(item.gwt || item.current_weight || ""),
            };
          case "lsWt":
            return { id: col.id, value: String(item.lwt || "") };
          case "netWt":
            return {
              id: col.id,
              value: String(item.nwt || item.exp_weight || ""),
            };
          case "remarks":
            return { id: col.id, value: String(item.remarks || "") };
          default:
            return { id: col.id, value: "" };
        }
      });
      return { id: idx + 1, data: mapped };
    });
    setTableData(rows.length ? rows : [{ id: 1, data: initialRowData }]);
  }, [editData, dropdownData.productGroup]);

  const handleOrderTypeChange = (e) => {
    setOrderType(e.target.value);
    // Reset selected mobile and name when type changes
    setSelectedMobile("");
    setSelectedName("");
    setSelectedPersonId(null);
    setSelectedPersonType(null);
    setShowMobileDropdown(false);
  };

  // Handle mobile dropdown selection
  const handleMobileSelect = (option) => {
    // Use mobile number from option - keep empty if no mobile number exists
    const mobileNumber = option.mobile || "";
    setSelectedMobile(mobileNumber);
    setSelectedName(option.name);
    setSelectedPersonId(option.id);
    setSelectedPersonType(option.personType || orderType); // Store person type (Supplier/Karigar)
    // Reflect into visitor fields while keeping them editable
    setVisitorNames(option.name || "");
    setVisitorMobiles(mobileNumber); // Use actual mobile number (empty if no number)
    setShowMobileDropdown(false);
    setMobileHighlightedIndex(0);
  };

  // Handle mobile input focus
  const handleMobileFocus = () => {
    const inputElement = mobileInputRef.current;
    if (inputElement) {
      inputElement.style.backgroundColor = "#fff";
    }
    setMobileHighlightedIndex(0);
    setShowMobileDropdown(true);
  };

  // Handle mobile input blur
  const handleMobileBlur = () => {
    const inputElement = mobileInputRef.current;
    if (inputElement) {
      inputElement.style.backgroundColor = "transparent";
    }
    // Delay hiding dropdown to allow click on options / keyboard interactions
    setTimeout(() => {
      setShowMobileDropdown(false);
    }, 150);
  };

  // Handle name input change (for search/filter; dropdown after 3+ characters)
  const handleMobileInputChange = (e) => {
    const searchTerm = e.target.value;
    setSelectedName(searchTerm);
    setSelectedPersonId(null);

    // Clear mobile when name is cleared
    if (!searchTerm) {
      setSelectedMobile("");
      setMobileDropdownOptions([]);
      setShowMobileDropdown(false);
      return;
    }

    // Require at least 3 characters before calling API
    if (searchTerm.length < 3) {
      setMobileDropdownOptions([]);
      setShowMobileDropdown(false);
      return;
    }

    // Fetch minimal data from API using prefix (name / mobile)
    fetchMobileOptionsByPrefix(orderType, searchTerm);
  };

  const handleMobileKeyDown = (e) => {
    const options = mobileDropdownOptions;

    if (!options.length) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        setShowMobileDropdown(false);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        setShowMobileDropdown(true);
        setMobileHighlightedIndex((prev) => {
          const next = prev < options.length - 1 ? prev + 1 : prev;
          if (mobileDropdownItemsRef.current[next]) {
            mobileDropdownItemsRef.current[next].scrollIntoView({
              block: "nearest",
            });
          }
          return next;
        });
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        setMobileHighlightedIndex((prev) => {
          const next = prev > 0 ? prev - 1 : 0;
          if (mobileDropdownItemsRef.current[next]) {
            mobileDropdownItemsRef.current[next].scrollIntoView({
              block: "nearest",
            });
          }
          return next;
        });
        break;
      }
      case "Enter": {
        if (showMobileDropdown && options[mobileHighlightedIndex]) {
          e.preventDefault();
          handleMobileSelect(options[mobileHighlightedIndex]);
        }
        break;
      }
      case "Escape": {
        e.preventDefault();
        setShowMobileDropdown(false);
        break;
      }
      case "Tab": {
        setShowMobileDropdown(false);
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        mobileDropdownRef.current &&
        !mobileDropdownRef.current.contains(event.target) &&
        mobileInputRef.current &&
        !mobileInputRef.current.contains(event.target)
      ) {
        setShowMobileDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const renderMobileDropdown = () => {
    // If dropdown is not active OR there are no options, don't render anything
    if (!showMobileDropdown || mobileDropdownOptions.length === 0) return null;

    const inputElement = mobileInputRef.current;
    if (!inputElement) return null;

    const inputRect = inputElement.getBoundingClientRect();
    const inputWidth = inputElement.offsetWidth;

    mobileDropdownItemsRef.current = [];

    const contentWidths = mobileDropdownOptions.map((option) => {
      const tempSpan = document.createElement("span");
      tempSpan.style.fontSize = "0.85rem";
      tempSpan.style.visibility = "hidden";
      tempSpan.style.position = "absolute";
      tempSpan.style.whiteSpace = "nowrap";
      tempSpan.textContent = option.label || "";
      document.body.appendChild(tempSpan);
      const width = tempSpan.offsetWidth;
      document.body.removeChild(tempSpan);
      return width;
    });

    const maxContentWidth =
      contentWidths.length > 0 ? Math.max(...contentWidths) : 0;

    const dropdownWidth = Math.max(
      inputWidth,
      Math.min(maxContentWidth + 24, 280), // cap width to keep menu compact
      220
    );

    return ReactDOM.createPortal(
      <div
        ref={mobileDropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "absolute",
          top: `${inputRect.bottom + window.scrollY}px`,
          left: `${inputRect.left + window.scrollX}px`,
          width: `${dropdownWidth}px`,
          minWidth: `${inputWidth}px`,
          maxWidth: `${dropdownWidth}px`,
          maxHeight: "180px",
          overflowY: "auto",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          padding: "0.5rem 0",
          backgroundColor: "#fff",
        }}
      >
        {mobileDropdownOptions.length > 0 ? (
          mobileDropdownOptions.map((option, idx) => (
            <div
              key={`${option.value}-${idx}`}
              ref={(el) => (mobileDropdownItemsRef.current[idx] = el)}
              className={`dropdown-item ${idx === mobileHighlightedIndex ? "active" : ""
                }`}
              style={{
                cursor: "pointer",
                padding: "0.25rem 1rem",
                fontSize: "0.85rem",
                backgroundColor:
                  idx === mobileHighlightedIndex ? "var(--theme-primary)" : "transparent",
                color: idx === mobileHighlightedIndex ? "#fff" : "inherit",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                transition: "background-color 0.2s, color 0.2s",
              }}
              onMouseEnter={() => setMobileHighlightedIndex(idx)}
              onMouseDown={(e) => {
                e.preventDefault();
                handleMobileSelect(option);
              }}
            >
              {option.label}
            </div>
          ))
        ) : (
          <div
            className="dropdown-item disabled"
            style={{
              padding: "0.25rem 1rem",
              fontSize: "0.85rem",
              whiteSpace: "normal",
              textAlign: "center",
            }}
          >
            No matching items
          </div>
        )}
      </div>,
      document.getElementById("dropdown-portal")
    );
  };

  // Visitor field handlers
  const handleNoOfVisitorsChange = (e) => {
    setNoOfVisitors(e.target.value);
  };

  const handleVisitorNamesChange = (e) => {
    setVisitorNames(e.target.value);
  };

  const handleVisitorMobilesChange = (e) => {
    const raw = e.target.value;
    // Allow digits, commas, and spaces to support multiple numbers
    const cleaned = raw.replace(/[^0-9, ]/g, "");
    const parts = cleaned
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    // Validate each part: up to 10 digits (while typing, allow shorter)
    const valid = parts.every((p) => /^\d{0,10}$/.test(p));
    if (valid || cleaned === "") {
      setVisitorMobiles(cleaned);
    }
  };

  const handleReferenceChange = (e) => {
    setReference(e.target.value);
  };

  const handleRemarksChange = (e) => {
    setRemarks(e.target.value);
  };

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const suggestionTimeoutRef = useRef(null);

  // Update the charge types data to include multiple codes
  const CHARGE_TYPES = [
    {
      name: "Hallmark Charges",
      codes: ["HC", "HM"],
    },
    {
      name: "Stone Charges",
      codes: ["SC"],
    },
    {
      name: "Diamond Charges",
      codes: ["DC"],
    },
    {
      name: "Jadtar Charges",
      codes: ["JC"],
    },
    {
      name: "Customization Charges",
      codes: ["CC"],
    },
    {
      name: "Engraving Charges",
      codes: ["EC"],
    },
    {
      name: "Polishing Charges",
      codes: ["PC"],
    },
    {
      name: "Electroplating Charges",
      codes: ["EPC"],
    },
    {
      name: "Antique/Oxidized Finish Charges",
      codes: ["AFC"],
    },
    {
      name: "Kadha/Hollow Jewellery Filling Charges",
      codes: ["KFC"],
    },
    {
      name: "Melting & Refining Charges",
      codes: ["MRC"],
    },
    {
      name: "Weighing & Testing Charges",
      codes: ["WTC"],
    },
    {
      name: "Laser Cutting Charges",
      codes: ["LCC"],
    },
    {
      name: "Handmade Labour Charges",
      codes: ["HLC"],
    },
    {
      name: "Antique Kundan Meena Charges",
      codes: ["AKMC"],
    },
    {
      name: "Threading & Bead Stringing Charges",
      codes: ["TBC"],
    },
    {
      name: "Enamel Meenakari Charges",
      codes: ["EMC"],
    },
    {
      name: "Embossing & Carving Charges",
      codes: ["ECC"],
    },
    {
      name: "Rhodium Charges",
      codes: ["RC"],
    },
  ];

  // Modify the getSuggestions function to handle multiple codes
  const getSuggestions = (query) => {
    if (!query || query.length < 1) {
      setSuggestions([]);
      return;
    }

    const searchQuery = query.toLowerCase();

    // First try to match by any code
    let matchedByCode = CHARGE_TYPES.filter((charge) =>
      charge.codes.some((code) => code.toLowerCase() === searchQuery)
    );

    // If no exact code match, then try partial matches on codes
    if (matchedByCode.length === 0) {
      matchedByCode = CHARGE_TYPES.filter((charge) =>
        charge.codes.some((code) => code.toLowerCase().startsWith(searchQuery))
      );
    }

    // Get matches by name
    const matchedByName = CHARGE_TYPES.filter(
      (charge) =>
        charge.name.toLowerCase().includes(searchQuery) &&
        !matchedByCode.some((codeMatch) => codeMatch.name === charge.name)
    );

    // Combine results with code matches first
    const matchedSuggestions = [...matchedByCode, ...matchedByName].map(
      (charge) => `${charge.name} (${charge.codes[0]})`
    );

    setSuggestions(matchedSuggestions);
    setShowSuggestions(true);
    setSelectedSuggestionIndex(0);
  };

  // Add function to handle suggestion selection
  const handleSuggestionSelect = (suggestion, rowIndex = null) => {
    // Extract just the name without the code in brackets
    const chargeName = suggestion.split(" (")[0];

    // Other Amount modal removed
    setShowSuggestions(false);
  };

  // Reset highlighted index when dropdown opens or filter changes
  useEffect(() => {
    if (activeDropdown.row !== null && activeDropdown.col !== null) {
      setHighlightedIndex(0);
      dropdownItemsRef.current = [];
    }
  }, [activeDropdown, tableData]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (dropdownRef.current && dropdownItemsRef.current[highlightedIndex]) {
      dropdownItemsRef.current[highlightedIndex].scrollIntoView({
        block: "nearest",
      });
    }
  }, [highlightedIndex]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown({ row: null, col: null, type: null });
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Create portal container for dropdown
  useEffect(() => {
    setupPortalContainers();
    return teardownPortalContainers;
  }, []);

  // Add this function to calculate Net Weight
  const calculateNetWeight = (gw, lw) => {
    return gw - lw; // NWt = GWt - LWt
  };

  // Function to calculate Labor Amount using Lbr %
  const calculateLbrAmtWithPercentage = (
    baseWeight,
    lbrPercentage,
    itRatePerGram
  ) => {
    return (
      (lbrPercentage / 100) * baseWeight * (parseFloat(itRatePerGram) || 0)
    );
  };

  // Function to calculate Labor Amount using Lbr Rate
  const calculateLbrAmtWithRate = (baseWeight, lbrRate) => {
    return lbrRate * baseWeight;
  };

  // Add this function to calculate Labor Amount
  const calculateLbrAmt = (
    baseWeight,
    lbrPercentage,
    lbrRate,
    itRatePerGram
  ) => {
    let lbrAmt = 0;
    if (lbrPercentage > 0) {
      lbrAmt = calculateLbrAmtWithPercentage(
        baseWeight,
        lbrPercentage,
        itRatePerGram
      );
    } else if (lbrRate > 0) {
      lbrAmt = calculateLbrAmtWithRate(baseWeight, lbrRate);
    }
    return lbrAmt;
  };

  // Update the handleInputChange function to calculate Lbr Amt
  const handleInputChange = (rowIndex, colIndex, value) => {
    // Get the column type
    const columnType = COLUMNS[colIndex].id;

    // Validation rules for different fields
    const validations = {
      design: /^[a-zA-Z0-9\s-_]*$/, // Allow alphabets, numbers, spaces, hyphens and underscores
      size: /^\d*$/, // Only numbers
      pcs: /^\d*$/, // Only numbers
      grWt: /^\d*\.?\d*$/, // Numbers and dot
      lsWt: /^\d*\.?\d*$/, // Numbers and dot
      netWt: /^\d*\.?\d*$/, // Numbers and dot
      lbrPercentage: /^\d*\.?\d*$/, // Numbers and dot
      lbrRate: /^\d*\.?\d*$/, // Numbers and dot
      lbrAmt: /^\d*\.?\d*$/, // Numbers and dot
      totalamt: /^\d*\.?\d*$/, // Numbers and dot
    };

    // Check if this column has a validation rule
    if (validations[columnType]) {
      // If value doesn't match validation pattern and isn't empty, don't update
      if (value !== "" && !validations[columnType].test(value)) {
        return;
      }
    }

    setTableData((prevData) => {
      let updatedData = [...prevData];
      if (updatedData[rowIndex] && updatedData[rowIndex].data) {
        // Get current lType value before updating
        const currentLType = updatedData[rowIndex].data.find(
          (cell) => cell.id === "lType"
        )?.value;

        // Check if trying to update lbrPercentage or lbrRate when lType is "Fixed"
        if (
          currentLType === "Fixed" &&
          (columnType === "lbrPercentage" || columnType === "lbrRate")
        ) {
          // Don't allow updating these fields when lType is "Fixed"
          return updatedData;
        }

        // Check if trying to update lbrAmt when lType is NOT "Fixed"
        if (currentLType !== "Fixed" && columnType === "lbrAmt") {
          // Don't allow updating lbrAmt when lType is not "Fixed"
          return updatedData;
        }

        // Handle tagNo: fetch options when user types 3+ characters
        if (columnType === "tagNo") {
          updatedData[rowIndex].data[colIndex].value = value;
          setTableData(updatedData);

          if (value && value.length >= 3) {
            setTagNoApiCalled(false);
            fetchTagNoByPrefix(value);
          } else {
            setLastSearchedTagPrefix("");
            setTagNoApiCalled(false);
          }
          return updatedData;
        }

        // Handle productGroup: fetch options when user types 3+ characters
        if (columnType === "productGroup") {
          updatedData[rowIndex].data[colIndex].value = value;
          setTableData(updatedData);

          // Trigger API call when user types 3+ characters
          if (value && value.length >= 3) {
            // Only reset if the new value doesn't start with the last searched prefix
            if (
              !lastSearchedProductGroupPrefix ||
              value !== lastSearchedProductGroupPrefix
            ) {
              setProductGroupApiCalled(false); // Reset flag before API call
              fetchProductGroupByPrefix(value);
            }
          } else {
            // Clear dropdown if less than 3 characters and reload all product groups
            fetchProductGroupByPrefix(); // Fetch all when no prefix
            setLastSearchedProductGroupPrefix("");
            setProductGroupApiCalled(false); // Reset flag when clearing
          }

          // Auto-calculate itrate from live price when product group changes
          // Use calculateAndUpdateItRate function for proper calculation
          updatedData = calculateAndUpdateItRate(updatedData, rowIndex);
          return updatedData;
        }

        // Proceed with the update for other cases
        updatedData[rowIndex].data[colIndex].value = value;

        // If we're changing the lType to "Fixed", clear lbrPercentage and lbrRate fields
        if (columnType === "lType" && value === "Fixed") {
          const lbrPercentageCell = updatedData[rowIndex].data.find(
            (cell) => cell.id === "lbrPercentage"
          );
          if (lbrPercentageCell) {
            lbrPercentageCell.value = "";
          }

          const lbrRateCell = updatedData[rowIndex].data.find(
            (cell) => cell.id === "lbrRate"
          );
          if (lbrRateCell) {
            lbrRateCell.value = "";
          }
        }

        // Calculate GWt and LWt
        const gwValue =
          parseFloat(
            updatedData[rowIndex].data.find((cell) => cell.id === "grWt")?.value
          ) || 0;
        const lwValue =
          parseFloat(
            updatedData[rowIndex].data.find((cell) => cell.id === "lsWt")?.value
          ) || 0;

        // Calculate NWt based on GWt and LWt
        const netWeight = calculateNetWeight(gwValue, lwValue);

        if (columnType === "grWt" || columnType === "lsWt") {
          // Update NWt when GWt or LWt changes
          const netWtCell = updatedData[rowIndex].data.find(
            (cell) => cell.id === "netWt"
          );
          if (netWtCell) {
            netWtCell.value = netWeight.toFixed(2); // Update NWt with 2 decimal places
          }
        }

        // Calculate itAmt when itrate or netWt changes
        if (
          columnType === "itrate" ||
          columnType === "netWt" ||
          columnType === "grWt" ||
          columnType === "lsWt"
        ) {
          const itRateCell = updatedData[rowIndex].data.find(
            (cell) => cell.id === "itrate"
          );
          const netWtCell = updatedData[rowIndex].data.find(
            (cell) => cell.id === "netWt"
          );
          const itAmtCell = updatedData[rowIndex].data.find(
            (cell) => cell.id === "itAmt"
          );

          if (itRateCell && netWtCell && itAmtCell) {
            const itRate = parseFloat(itRateCell.value) || 0;
            const netWt = parseFloat(netWtCell.value) || 0;
            const itAmt = (itRate * netWt).toFixed(2);
            itAmtCell.value = itAmt;
          }
        }

        // Calculate Lbr Amt based on Lbr Type
        const lbrType = updatedData[rowIndex].data.find(
          (cell) => cell.id === "lType"
        )?.value;
        const lbrPercentage =
          parseFloat(
            updatedData[rowIndex].data.find(
              (cell) => cell.id === "lbrPercentage"
            )?.value
          ) || 0;
        const lbrRate =
          parseFloat(
            updatedData[rowIndex].data.find((cell) => cell.id === "lbrRate")
              ?.value
          ) || 0;
        let baseWeight = 0;

        // Determine Base Weight based on Lbr Type
        if (lbrType === "NWt") {
          baseWeight = netWeight;
        } else if (lbrType === "GWt") {
          baseWeight = gwValue;
        } else if (lbrType === "Pcs") {
          baseWeight =
            parseFloat(
              updatedData[rowIndex].data.find((cell) => cell.id === "pcs")
                ?.value
            ) || 0;
        }

        // Get itRate for labor calculations
        const itRateCellForLbr = updatedData[rowIndex].data.find(
          (cell) => cell.id === "itrate"
        );
        const itRatePerGram = parseFloat(itRateCellForLbr?.value) || 0;

        // Sync lbrRate and lbrPercentage fields
        if (columnType === "lbrPercentage" && lbrType !== "Fixed") {
          const lbrRateCell = updatedData[rowIndex].data.find(
            (cell) => cell.id === "lbrRate"
          );
          if (lbrRateCell) {
            lbrRateCell.value = (
              (parseFloat(value) / 100) *
              itRatePerGram
            ).toFixed(2);
          }
        }
        if (columnType === "lbrRate" && lbrType !== "Fixed") {
          const lbrPercentageCell = updatedData[rowIndex].data.find(
            (cell) => cell.id === "lbrPercentage"
          );
          if (lbrPercentageCell) {
            lbrPercentageCell.value = (
              (parseFloat(value) / itRatePerGram) *
              100
            ).toFixed(2);
          }
        }

        // Calculate Lbr Amt
        let lbrAmt = 0;
        if (lbrPercentage > 0) {
          lbrAmt = calculateLbrAmtWithPercentage(
            baseWeight,
            lbrPercentage,
            itRatePerGram
          );
        } else if (lbrRate > 0) {
          lbrAmt = calculateLbrAmtWithRate(baseWeight, lbrRate);
        }

        // Update Lbr Amt cell automatically for non-Fixed types
        const lbrAmtCell = updatedData[rowIndex].data.find(
          (cell) => cell.id === "lbrAmt"
        );
        if (lbrAmtCell) {
          // Round to the nearest whole number for display
          lbrAmtCell.value = Math.round(lbrAmt).toString();
        }

        // Calculate totalamt (itAmt + lbrAmt)
        const itAmtCell = updatedData[rowIndex].data.find(
          (cell) => cell.id === "itAmt"
        );
        const totalAmtCell = updatedData[rowIndex].data.find(
          (cell) => cell.id === "totalamt"
        );
        if (totalAmtCell && itAmtCell && lbrAmtCell) {
          const itAmtVal = parseFloat(itAmtCell.value || 0);
          const lbrAmtVal = parseFloat(lbrAmtCell.value || 0);
          const totalAmt = (itAmtVal + lbrAmtVal).toFixed(2);
          totalAmtCell.value = totalAmt.toString();
        }
      }
      return updatedData;
    });

    // Check if this is the last column of the current row and it's the last row
    if (colIndex === COLUMNS.length - 1 && rowIndex === tableData.length - 1) {
      // Automatically add a new row when cursor is in the last column of the last row
      handleAddRow();
    }
  };

  const [imageName, setImageName] = useState("");

  // Open image modal
  const openImageModal = (imageUrl) => {
    // Clear any active tooltips when opening modal
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    setActiveTooltip(null);

    // Hide any visible tooltips
    Object.values(tooltipRefs.current).forEach((tooltip) => {
      if (tooltip) tooltip.style.display = "none";
    });

    setImageModal({
      isOpen: true,
      imageUrl,
      zoomLevel: 1,
    });
  };

  // Close image modal
  const closeImageModal = () => {
    setImageModal({
      isOpen: false,
      imageUrl: null,
      zoomLevel: 1,
    });
  };

  // Render image modal
  const renderImageModal = () => {
    if (!imageModal.isOpen) return null;

    return ReactDOM.createPortal(
      <Modal show={imageModal.isOpen} onHide={closeImageModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Image Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <img
              src={imageModal.imageUrl}
              alt="Preview"
              style={{
                maxWidth: "100%",
                maxHeight: "400px",
                objectFit: "contain",
              }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-center gap-2">
            <Button
              variant="primary"
              size="sm"
              className={buttonClass}
              onClick={() => {
                // Trigger file input to change the image
                const rowIndex = tableData.findIndex((row) =>
                  row.data.some(
                    (cell) =>
                      cell.id === "image" &&
                      cell.imagePreview === imageModal.imageUrl
                  )
                );

                if (rowIndex !== -1) {
                  const colIndex = tableData[rowIndex].data.findIndex(
                    (cell) => cell.id === "image"
                  );
                  if (colIndex !== -1) {
                    triggerFileInput(rowIndex, colIndex);
                    closeImageModal();
                  }
                }
              }}
              title="Change Image"
            >
              <Icon icon="solar:gallery-edit-bold" width="16" height="16" />
            </Button>
            <Button
              variant="danger"
              size="sm"
              className={buttonClass}
              onClick={() => {
                // Find and remove the image
                const rowIndex = tableData.findIndex((row) =>
                  row.data.some(
                    (cell) =>
                      cell.id === "image" &&
                      cell.imagePreview === imageModal.imageUrl
                  )
                );

                if (rowIndex !== -1) {
                  const colIndex = tableData[rowIndex].data.findIndex(
                    (cell) => cell.id === "image"
                  );
                  if (colIndex !== -1) {
                    setTableData((prevData) => {
                      const updatedData = [...prevData];
                      const imageCell = updatedData[rowIndex].data.find(
                        (c) => c.id === "image"
                      );
                      if (imageCell) {
                        imageCell.value = "";
                        imageCell.imageFile = null;
                        imageCell.imagePreview = null;
                      }
                      return updatedData;
                    });
                    closeImageModal();
                  }
                }
              }}
              title="Delete Image"
            >
              <Icon icon="solar:trash-bin-trash-bold" width="16" height="16" />
            </Button>
          </div>
        </Modal.Footer>
      </Modal>,
      document.getElementById("modal-portal")
    );
  };

  const handleAddRow = () => {
    setTableData((prevData) => [
      ...prevData,
      {
        id: prevData.length + 1,
        data: COLUMNS.map((col) => ({
          id: col.id,
          value: "",
          // Add imageFile property for image column
          ...(col.id === "image"
            ? { imageFile: null, imagePreview: null }
            : {}),
        })),
      },
    ]);
  };

  // Function to focus on the first cell of the next row
  const focusFirstCellOfNextRow = (currentRowIndex) => {
    setTimeout(() => {
      const nextRowIndex = currentRowIndex + 1;
      if (nextRowIndex < tableData.length) {
        const firstCellInput = inputRefs.current[`${nextRowIndex}-0`];
        if (firstCellInput) firstCellInput.focus();
      }
    }, 0);
  };

  const handleDropdownClick = (rowIndex, colIndex, columnType) => {
    // Get position of the input element for positioning the dropdown
    const inputElement = inputRefs.current[`${rowIndex}-${colIndex}`];
    if (inputElement) {
      const rect = inputElement.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }

    setActiveDropdown({ row: rowIndex, col: colIndex, type: columnType });
    setHighlightedIndex(0);

    // Lazy-load product groups only when user opens Product Group dropdown.
    if (
      columnType === "productGroup" &&
      (!Array.isArray(dropdownData.productGroup) ||
        dropdownData.productGroup.length === 0)
    ) {
      fetchProductGroupByPrefix();
    }
  };

  const handleDropdownSelect = (rowIndex, colIndex, selectedItem) => {
    // Ensure we're passing the label or value, not the entire object
    const value =
      typeof selectedItem === "object" ? selectedItem.label : selectedItem;

    handleInputChange(rowIndex, colIndex, value);

    // If tagNo was selected, fetch full details
    const columnType = COLUMNS[colIndex].id;
    if (columnType === "tagNo") {
      sendTagNoToApi(value, rowIndex);
    }

    setActiveDropdown({ row: null, col: null, type: null });

    // Focus on the next cell after selection
    setTimeout(() => {
      const nextColIndex = colIndex + 1;
      if (nextColIndex < COLUMNS.length) {
        const nextInput = inputRefs.current[`${rowIndex}-${nextColIndex}`];
        if (nextInput) nextInput.focus();
      } else {
        // If it's the last cell in the row, focus on the first cell of the next row
        focusFirstCellOfNextRow(rowIndex);
      }
    }, 0);
  };

  // Get filtered items based on input value and column type
  const getFilteredItems = (searchText, columnType) => {
    // console.log("Column Type:", columnType);
    // console.log("Dropdown Data:", dropdownData);
    // console.log("Search Text:", searchText);

    if (!dropdownData[columnType]) return [];

    // Ensure searchText is a string
    const normalizedSearchText = searchText
      ? String(searchText).toLowerCase()
      : "";

    if (!normalizedSearchText) return dropdownData[columnType];

    // Special handling for lType (which is an array of strings)
    if (columnType === "lType") {
      return dropdownData[columnType].filter((item) =>
        item.toLowerCase().includes(normalizedSearchText)
      );
    }

    // Special handling for productGroup (search both label and short_code)
    if (columnType === "productGroup") {
      return dropdownData[columnType].filter((item) => {
        const labelMatch =
          (item.label &&
            String(item.label).toLowerCase().includes(normalizedSearchText)) ||
          false;
        const shortCodeMatch =
          (item.short_code &&
            String(item.short_code)
              .toLowerCase()
              .includes(normalizedSearchText)) ||
          false;
        return labelMatch || shortCodeMatch;
      });
    }

    // Special handling for supplier (search both label and short_code)
    if (columnType === "supplier") {
      return dropdownData[columnType].filter((item) => {
        const labelMatch =
          (item.label &&
            String(item.label).toLowerCase().includes(normalizedSearchText)) ||
          false;
        const shortCodeMatch =
          (item.short_code &&
            String(item.short_code)
              .toLowerCase()
              .includes(normalizedSearchText)) ||
          false;
        return labelMatch || shortCodeMatch;
      });
    }

    // For other dropdown types
    return dropdownData[columnType].filter((item) => {
      // Safely handle both object and string types
      if (typeof item === "object") {
        return item.label
          ? String(item.label).toLowerCase().includes(normalizedSearchText)
          : false;
      }
      return String(item).toLowerCase().includes(normalizedSearchText);
    });
  };

  // Add this function to get filtered items for Rate On dropdown
  const getFilteredRateOnItems = (searchText) => {
    if (!searchText) return rateOnOptions;
    return rateOnOptions.filter((item) =>
      item.toLowerCase().includes(searchText.toLowerCase())
    );
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, rowIndex, colIndex, columnType) => {
    // Handle Tab key to move to next cell and potentially add a new row
    if (e.key === "Tab" && !e.shiftKey) {
      // If we're at the last column of the last row
      if (
        colIndex === COLUMNS.length - 1 &&
        rowIndex === tableData.length - 1
      ) {
        e.preventDefault();
        handleAddRow();
        // Focus will be set to the first cell of the new row after render
        setTimeout(() => {
          const newRowIndex = tableData.length;
          const firstCellInput = inputRefs.current[`${newRowIndex}-0`];
          if (firstCellInput) firstCellInput.focus();
        }, 0);
      }
    }

    // Handle dropdown keyboard navigation
    if (columnType === "rateOn" || dropdownColumns.includes(columnType)) {
      const cell = tableData[rowIndex]?.data[colIndex];
      if (!cell) return;

      const filtered =
        columnType === "rateOn"
          ? getFilteredRateOnItems(cell.value)
          : getFilteredItems(cell.value, columnType);

      if (filtered.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < filtered.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
          break;
        case "Enter":
          e.preventDefault();
          if (filtered[highlightedIndex]) {
            handleDropdownSelect(
              rowIndex,
              colIndex,
              filtered[highlightedIndex]
            );
          }
          break;
        case "Escape":
          e.preventDefault();
          setActiveDropdown({ row: null, col: null, type: null });
          break;
        case "Tab":
          setActiveDropdown({ row: null, col: null, type: null });
          break;
        default:
          break;
      }
    }
  };

  // Render dropdown through portal
  const renderDropdown = () => {
    if (
      activeDropdown.row === null ||
      activeDropdown.col === null ||
      !activeDropdown.type
    )
      return null;

    const cell = tableData[activeDropdown.row]?.data[activeDropdown.col];
    if (!cell) return null;

    const filtered = getFilteredItems(cell.value, activeDropdown.type);

    // Reset refs array
    dropdownItemsRef.current = [];

    // Calculate max width based on content
    const contentWidths = filtered.map((item) => {
      const tempSpan = document.createElement("span");
      tempSpan.style.fontSize = "0.85rem";
      tempSpan.style.visibility = "hidden";
      tempSpan.style.position = "absolute";
      tempSpan.style.whiteSpace = "nowrap";

      // Safely get the label
      const itemLabel = typeof item === "object" ? item.label || "" : item;
      tempSpan.textContent = itemLabel;

      document.body.appendChild(tempSpan);
      const width = tempSpan.offsetWidth;
      document.body.removeChild(tempSpan);
      return width;
    });

    const maxContentWidth =
      contentWidths.length > 0
        ? Math.max(...contentWidths)
        : dropdownPosition.width;

    const adjustedWidth = Math.max(
      dropdownPosition.width,
      maxContentWidth + 20
    );

    return ReactDOM.createPortal(
      <div
        ref={dropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "absolute",
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          width: `${adjustedWidth}px`,
          maxHeight: "165px",
          height: "165px",
          overflow: "hidden",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          backgroundColor: "#fff",
          boxSizing: "border-box",
          boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
        }}
      >
        <div
          style={{
            maxHeight: "165px",
            height: "165px",
            overflowY: "scroll",
            overflowX: "hidden",
          }}
        >
          {filtered.length > 0 ? (
            filtered.map((item, idx) => {
              // Handle both object and string types
              const itemLabel =
                typeof item === "object" ? item.label || "" : item;
              const isSelected =
                typeof item === "object"
                  ? (item.label || "").toLowerCase() ===
                  (cell.value || "").toLowerCase()
                  : item.toLowerCase() === (cell.value || "").toLowerCase();

              return (
                <div
                  key={idx}
                  ref={(el) => (dropdownItemsRef.current[idx] = el)}
                  className={`dropdown-item ${idx === highlightedIndex ? "active" : ""
                    }`}
                  style={{
                    cursor: "pointer",
                    padding: "0.4rem 0.75rem",
                    fontSize: "0.85rem",
                    backgroundColor:
                      idx === highlightedIndex
                        ? "var(--theme-primary)"
                        : isSelected
                          ? "#f0f0f0"
                          : "transparent",
                    color: idx === highlightedIndex ? "#fff" : "inherit",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                  onMouseDown={() =>
                    handleDropdownSelect(
                      activeDropdown.row,
                      activeDropdown.col,
                      item
                    )
                  }
                >
                  {itemLabel}
                </div>
              );
            })
          ) : (
            <div
              className="dropdown-item disabled"
              style={{
                padding: "0.4rem 0.75rem",
                fontSize: "0.85rem",
                textAlign: "center",
              }}
            >
              No matching items
            </div>
          )}
        </div>
      </div>,
      document.getElementById("dropdown-portal")
    );
  };

  // Check if a column should have dropdown functionality
  const isDropdownColumn = (columnId) => {
    return dropdownColumns.includes(columnId);
  };

  // Check if a column is the image column
  const isImageColumn = (columnId) => {
    return columnId === "image";
  };

  // Get column type from index
  const getColumnType = (colIndex) => {
    return COLUMNS[colIndex]?.id;
  };

  // Add a function to handle focus on the last column
  const handleFocus = (rowIndex, colIndex) => {
    // Change background color
    const inputElement = inputRefs.current[`${rowIndex}-${colIndex}`];
    if (inputElement) {
      inputElement.style.backgroundColor = "#fff";
    }

    // If this is the last column of the last row, add a new row
    if (colIndex === COLUMNS.length - 1 && rowIndex === tableData.length - 1) {
      handleAddRow();
    }

    // If this is a dropdown column, show the dropdown
    // For productGroup and tagNo, only show dropdown if user has typed 3+ characters and we have results
    const columnType = getColumnType(colIndex);
    if (isDropdownColumn(columnType)) {
      if (columnType === "productGroup" || columnType === "tagNo") {
        // For productGroup and tagNo, only show dropdown if user has typed 3+ characters AND we have results or API was called
        const cell = tableData[rowIndex]?.data?.find(
          (c) => c.id === columnType
        );
        const cellValue = (cell?.value || "").toString().trim();
        const hasEnoughChars = cellValue.length >= 3;
        const hasResults = getFilteredItems(cellValue, columnType).length > 0;

        // Check appropriate API called flag
        const apiCalled =
          columnType === "productGroup"
            ? productGroupApiCalled
            : tagNoApiCalled;

        // Only show dropdown if user has typed 3+ characters AND (has results OR API was called)
        if (hasEnoughChars && (hasResults || apiCalled)) {
          handleDropdownClick(rowIndex, colIndex, columnType);
        }
        // Don't show dropdown if user just clicked/focused without typing 3+ characters
      } else {
        // For other dropdown columns, show dropdown normally
        handleDropdownClick(rowIndex, colIndex, columnType);
      }
    }
  };

  // Trigger file input click
  const triggerFileInput = (rowIndex, colIndex) => {
    const fileInput = fileInputRefs.current[`${rowIndex}-${colIndex}`];
    if (fileInput) {
      fileInput.click();
    }
  };

  // Modify the handleOtherAmountInteraction function
  const handleOtherAmountInteraction = (rowIndex, event) => {
    if (event.type === "dblclick") {
      event.preventDefault();
      // Other Amount modal removed
    } else if (event.type === "keydown" && event.key === "Enter") {
      const now = Date.now();
      if (
        lastEnterPress.rowIndex === rowIndex &&
        now - lastEnterPress.time < 500
      ) {
        // 500ms threshold for double-Enter
        event.preventDefault();
        // Other Amount modal removed
        setLastEnterPress({ time: 0, rowIndex: null });
      } else {
        setLastEnterPress({ time: now, rowIndex });
      }
    } else if (event.type === "click") {
      // Show tooltip for 4 seconds on click
      const tooltipId = `oth-amt-tooltip-${rowIndex}`;
      const tooltip = tooltipRefs.current[tooltipId];

      if (tooltip) {
        // Clear any existing timeout
        if (tooltipTimeoutRef.current) {
          clearTimeout(tooltipTimeoutRef.current);
        }

        // Check if tooltip is already visible
        if (tooltip.style.display === "block") {
          // Reset the timeout to keep it visible
          tooltipTimeoutRef.current = setTimeout(() => {
            tooltip.style.display = "none";
            setActiveTooltip(null);
          }, 4000);
          return; // Exit to prevent further actions
        }

        tooltip.style.display = "block"; // Show tooltip immediately
        setActiveTooltip(tooltipId); // Set active tooltip

        // Set timeout to hide after 4 seconds
        tooltipTimeoutRef.current = setTimeout(() => {
          tooltip.style.display = "none";
          setActiveTooltip(null);
        }, 4000);
      }
    }
  };

  // Other Amount modal function removed as field is not needed

  // Other Amount functions removed as field is not needed

  // Other Amount modal render function removed as field is not needed
  const renderOtherAmountModal = () => {
    return (
      <Modal show={false} onHide={() => { }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Other Amount Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className={`table-responsive border rounded-0 ${cardClass}`}>
            <table
              className={`table text-nowrap mb-0 align-middle table-bordered ${tableClass} theme-header-table`}
            >
              <thead className="text-dark fs-4">
                <tr>
                  {[
                    "No.",
                    "Charge Type",
                    "Pcs",
                    "Ct",
                    "Wt",
                    "Con%",
                    "Rate On",
                    "Rate",
                    "Amount",
                  ].map((header, index) => (
                    <th
                      key={header}
                      className="px-2 py-2 text-center"
                      style={{
                        fontSize: "0.85rem",
                        width:
                          otherAmountColumnWidths[
                          header.toLowerCase().replace(/[^a-z]/g, "")
                          ],
                        fontWeight: "600",
                        whiteSpace: "nowrap",
                        position: "relative",
                        userSelect: "none",
                        border: "1px solid #dee2e6",
                      }}
                    >
                      {header}
                      <div
                        className="resize-handle"
                        style={{
                          position: "absolute",
                          right: 0,
                          top: 0,
                          bottom: 0,
                          width: "5px",
                          cursor: "col-resize",
                          backgroundColor: "transparent",
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          const startX = e.clientX;
                          const startWidth =
                            e.currentTarget.parentElement.offsetWidth;
                          const column = header
                            .toLowerCase()
                            .replace(/[^a-z]/g, "");

                          const handleMouseMove = (e) => {
                            const diff = e.clientX - startX;
                            const newWidth = Math.max(50, startWidth + diff);
                            handleOtherAmountColumnWidthChange(
                              column,
                              newWidth
                            );
                          };

                          const handleMouseUp = () => {
                            document.removeEventListener(
                              "mousemove",
                              handleMouseMove
                            );
                            document.removeEventListener(
                              "mouseup",
                              handleMouseUp
                            );
                          };

                          document.addEventListener(
                            "mousemove",
                            handleMouseMove
                          );
                          document.addEventListener("mouseup", handleMouseUp);
                        }}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {otherAmountModal.entries.map((entry, index) => (
                  <tr key={index} style={{ backgroundColor: "#ffffff" }}>
                    <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ height: "38px" }}
                      >
                        <span style={{ fontSize: "0.85rem" }}>{index + 1}</span>
                      </div>
                    </td>
                    <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                      <InlineAutoCompleteInput
                        id={`entry-${index}`} // Add unique ID for existing entries
                        value={entry.chargeType}
                        suggestion={
                          suggestions.length > 0 ? suggestions[0] : ""
                        }
                        onChange={(e) => {
                          const newValue = e.target.value;
                          const newEntries = [...otherAmountModal.entries];
                          newEntries[index].chargeType = newValue;
                          setOtherAmountModal((prev) => ({
                            ...prev,
                            entries: newEntries,
                          }));
                          getSuggestions(newValue);
                        }}
                        onComplete={(suggestion) => {
                          const newEntries = [...otherAmountModal.entries];
                          newEntries[index].chargeType = suggestion;
                          setOtherAmountModal((prev) => ({
                            ...prev,
                            entries: newEntries,
                          }));
                          setSuggestions([]);
                        }}
                        className={`form-control border-0 ${buttonClass}`}
                        style={{
                          fontSize: "0.85rem",
                          padding: "0.4rem 0.5rem",
                          backgroundColor: "transparent",
                          width: "100%",
                          transition: "background-color 0.2s",
                        }}
                        onFocus={(e) => {
                          e.target.style.backgroundColor = "#fff";
                          if (entry.chargeType) {
                            getSuggestions(entry.chargeType);
                          }
                        }}
                        onBlur={(e) => {
                          e.target.style.backgroundColor = "transparent";
                          setTimeout(() => setSuggestions([]), 200);
                        }}
                      />
                    </td>
                    <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                      <input
                        type="text"
                        value={entry.pcs}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || /^\d*$/.test(value)) {
                            const newEntries = [...otherAmountModal.entries];
                            const updatedEntry = {
                              ...newEntries[index],
                              pcs: value,
                            };
                            // Recalculate amount based on Rate On
                            if (updatedEntry.rate && updatedEntry.rateOn) {
                              updatedEntry.amount = formatAmount(
                                calculateAmount(updatedEntry)
                              );
                            }
                            newEntries[index] = updatedEntry;
                            setOtherAmountModal((prev) => ({
                              ...prev,
                              entries: newEntries,
                            }));
                          }
                        }}
                        className={`form-control border-0 ${buttonClass}`}
                        style={{
                          fontSize: "0.85rem",
                          padding: "0.4rem 0.5rem",
                          backgroundColor: "transparent",
                          width: "100%",
                          transition: "background-color 0.2s",
                          textAlign: "left",
                        }}
                        onFocus={(e) =>
                          (e.target.style.backgroundColor = "#fff")
                        }
                        onBlur={(e) =>
                          (e.target.style.backgroundColor = "transparent")
                        }
                      />
                    </td>
                    <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                      <input
                        type="text"
                        value={entry.ct}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || /^\d*\.?\d*$/.test(value)) {
                            const newEntries = [...otherAmountModal.entries];
                            const updatedEntry = {
                              ...newEntries[index],
                              ct: value,
                            };
                            // Recalculate amount based on Rate On
                            if (updatedEntry.rate && updatedEntry.rateOn) {
                              updatedEntry.amount = formatAmount(
                                calculateAmount(updatedEntry)
                              );
                            }
                            newEntries[index] = updatedEntry;
                            setOtherAmountModal((prev) => ({
                              ...prev,
                              entries: newEntries,
                            }));
                          }
                        }}
                        className={`form-control border-0 ${buttonClass}`}
                        style={{
                          fontSize: "0.85rem",
                          padding: "0.4rem 0.5rem",
                          backgroundColor: "transparent",
                          width: "100%",
                          transition: "background-color 0.2s",
                          textAlign: "left",
                        }}
                        onFocus={(e) =>
                          (e.target.style.backgroundColor = "#fff")
                        }
                        onBlur={(e) =>
                          (e.target.style.backgroundColor = "transparent")
                        }
                      />
                    </td>
                    <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                      <input
                        type="text"
                        value={entry.wt}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || /^\d*\.?\d*$/.test(value)) {
                            const newEntries = [...otherAmountModal.entries];
                            const updatedEntry = {
                              ...newEntries[index],
                              wt: value,
                            };
                            // Recalculate amount based on Rate On
                            if (updatedEntry.rate && updatedEntry.rateOn) {
                              updatedEntry.amount = formatAmount(
                                calculateAmount(updatedEntry)
                              );
                            }
                            newEntries[index] = updatedEntry;
                            setOtherAmountModal((prev) => ({
                              ...prev,
                              entries: newEntries,
                            }));
                          }
                        }}
                        className={`form-control border-0 ${buttonClass}`}
                        style={{
                          fontSize: "0.85rem",
                          padding: "0.4rem 0.5rem",
                          backgroundColor: "transparent",
                          width: "100%",
                          transition: "background-color 0.2s",
                          textAlign: "left",
                        }}
                        onFocus={(e) =>
                          (e.target.style.backgroundColor = "#fff")
                        }
                        onBlur={(e) =>
                          (e.target.style.backgroundColor = "transparent")
                        }
                      />
                    </td>
                    <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                      <input
                        type="text"
                        value={entry.conPercentage}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || /^\d*\.?\d*$/.test(value)) {
                            const newEntries = [...otherAmountModal.entries];
                            newEntries[index] = {
                              ...newEntries[index],
                              conPercentage: value,
                            };
                            setOtherAmountModal((prev) => ({
                              ...prev,
                              entries: newEntries,
                            }));
                          }
                        }}
                        className={`form-control border-0 ${buttonClass}`}
                        style={{
                          fontSize: "0.85rem",
                          padding: "0.4rem 0.5rem",
                          backgroundColor: "transparent",
                          width: "100%",
                          transition: "background-color 0.2s",
                          textAlign: "left",
                        }}
                        onFocus={(e) =>
                          (e.target.style.backgroundColor = "#fff")
                        }
                        onBlur={(e) =>
                          (e.target.style.backgroundColor = "transparent")
                        }
                      />
                    </td>
                    <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                      <div className="position-relative">
                        <input
                          type="text"
                          value={entry.rateOn}
                          onChange={(e) => {
                            const newEntries = [...otherAmountModal.entries];
                            newEntries[index].rateOn = e.target.value;
                            setOtherAmountModal((prev) => ({
                              ...prev,
                              entries: newEntries,
                            }));
                          }}
                          className={`form-control border-0 ${buttonClass}`}
                          style={{
                            fontSize: "0.85rem",
                            padding: "0.4rem 0.5rem",
                            backgroundColor: "transparent",
                            width: "100%",
                            transition: "background-color 0.2s",
                          }}
                          onFocus={(e) => {
                            e.target.style.backgroundColor = "#fff";
                            const rect = e.target.getBoundingClientRect();
                            const dropdown = e.target.nextElementSibling;
                            if (dropdown) {
                              // Calculate max width based on content
                              const maxWidth = Math.max(
                                ...rateOnOptions.map((item) => {
                                  const tempSpan =
                                    document.createElement("span");
                                  tempSpan.style.fontSize = "0.85rem";
                                  tempSpan.style.visibility = "hidden";
                                  tempSpan.style.position = "absolute";
                                  tempSpan.style.whiteSpace = "nowrap";
                                  tempSpan.textContent = item;
                                  document.body.appendChild(tempSpan);
                                  const width = tempSpan.offsetWidth;
                                  document.body.removeChild(tempSpan);
                                  return width;
                                }),
                                rect.width // minimum width is input width
                              );

                              dropdown.style.display = "block";
                              dropdown.style.position = "fixed";
                              dropdown.style.top = `${rect.bottom}px`;
                              dropdown.style.left = `${rect.left}px`;
                              dropdown.style.width = "auto";
                              dropdown.style.minWidth = `${rect.width}px`;
                              dropdown.style.maxWidth = `${maxWidth + 32}px`; // Add padding
                              dropdown.style.zIndex = "9999";
                              // Reset highlighted index when opening dropdown
                              setHighlightedIndex(0);
                            }
                          }}
                          onBlur={(e) => {
                            e.target.style.backgroundColor = "transparent";
                            setTimeout(() => {
                              const dropdown = e.target.nextElementSibling;
                              if (dropdown) {
                                dropdown.style.display = "none";
                              }
                            }, 200);
                          }}
                        />
                        <div
                          className="dropdown-menu shadow-sm"
                          style={{
                            display: "none",
                            maxHeight: "200px",
                            overflowY: "auto",
                            backgroundColor: "#fff",
                            position: "fixed",
                            zIndex: 9999,
                            border: "1px solid rgba(0,0,0,.15)",
                            borderRadius: "0.25rem",
                            padding: "0.5rem 0",
                            width: "auto",
                            minWidth: "100%",
                          }}
                        >
                          {rateOnOptions
                            .filter((option) =>
                              option
                                .toLowerCase()
                                .includes((entry.rateOn || "").toLowerCase())
                            )
                            .map((option, i) => (
                              <div
                                key={i}
                                className={`dropdown-item ${i === highlightedIndex ? "active" : ""
                                  }`}
                                style={{
                                  cursor: "pointer",
                                  padding: "0.25rem 1rem",
                                  fontSize: "0.85rem",
                                  backgroundColor:
                                    i === highlightedIndex
                                      ? "var(--theme-primary)"
                                      : option.toLowerCase() ===
                                        entry.rateOn.toLowerCase()
                                        ? "#f0f0f0"
                                        : "transparent",
                                  color:
                                    i === highlightedIndex ? "#fff" : "inherit",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                onMouseEnter={() => setHighlightedIndex(i)}
                                onMouseDown={() => {
                                  const newEntries = [
                                    ...otherAmountModal.entries,
                                  ];
                                  newEntries[index].rateOn = option;
                                  setOtherAmountModal((prev) => ({
                                    ...prev,
                                    entries: newEntries,
                                  }));
                                  // Hide dropdown after selection
                                  const dropdown = document.querySelector(
                                    '.dropdown-menu[style*="display: block"]'
                                  );
                                  if (dropdown) {
                                    dropdown.style.display = "none";
                                  }
                                }}
                              >
                                {option}
                              </div>
                            ))}
                        </div>
                      </div>
                    </td>
                    <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                      <input
                        type="text"
                        value={entry.rate}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || /^\d*\.?\d*$/.test(value)) {
                            const newEntries = [...otherAmountModal.entries];
                            const updatedEntry = {
                              ...newEntries[index],
                              rate: value,
                            };
                            // Calculate amount whenever rate changes
                            if (value && updatedEntry.rateOn) {
                              updatedEntry.amount = formatAmount(
                                calculateAmount(updatedEntry)
                              );
                            } else {
                              updatedEntry.amount = "";
                            }
                            newEntries[index] = updatedEntry;
                            setOtherAmountModal((prev) => ({
                              ...prev,
                              entries: newEntries,
                            }));
                          }
                        }}
                        className={`form-control border-0 ${buttonClass}`}
                        style={{
                          fontSize: "0.85rem",
                          padding: "0.4rem 0.5rem",
                          backgroundColor: "transparent",
                          width: "100%",
                          transition: "background-color 0.2s",
                          textAlign: "left",
                        }}
                        onFocus={(e) =>
                          (e.target.style.backgroundColor = "#fff")
                        }
                        onBlur={(e) =>
                          (e.target.style.backgroundColor = "transparent")
                        }
                      />
                    </td>
                    <td
                      className="p-0 position-relative"
                      style={{ border: "1px solid #dee2e6" }}
                    >
                      <input
                        type="text"
                        value={entry.amount}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || /^\d*\.?\d*$/.test(value)) {
                            const newEntries = [...otherAmountModal.entries];
                            newEntries[index].amount = value;
                            setOtherAmountModal((prev) => ({
                              ...prev,
                              entries: newEntries,
                            }));
                          }
                        }}
                        onFocus={(e) => {
                          e.target.style.backgroundColor = "#fff";
                        }}
                        onBlur={(e) => {
                          e.target.style.backgroundColor = "transparent";
                        }}
                        className={`form-control border-0 ${buttonClass}`}
                        style={{
                          fontSize: "0.85rem",
                          padding: "0.4rem 0.5rem",
                          backgroundColor: "transparent",
                          width: "100%",
                          transition: "background-color 0.2s",
                          textAlign: "left",
                        }}
                      />
                    </td>
                  </tr>
                ))}
                {/* Empty row for new entries */}
                <tr style={{ backgroundColor: "#ffffff" }}>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{ height: "38px" }}
                    >
                      <span style={{ fontSize: "0.85rem" }}>
                        {otherAmountModal.entries.length + 1}
                      </span>
                    </div>
                  </td>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <InlineAutoCompleteInput
                      id="current-entry" // Add unique ID for current entry
                      value={otherAmountModal.currentEntry.chargeType}
                      suggestion={suggestions.length > 0 ? suggestions[0] : ""}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setOtherAmountModal((prev) => ({
                          ...prev,
                          currentEntry: {
                            ...prev.currentEntry,
                            chargeType: newValue,
                          },
                        }));
                        getSuggestions(newValue);
                      }}
                      onComplete={(suggestion) => {
                        setOtherAmountModal((prev) => ({
                          ...prev,
                          currentEntry: {
                            ...prev.currentEntry,
                            chargeType: suggestion,
                          },
                        }));
                        setSuggestions([]);
                      }}
                      className={`form-control border-0 ${buttonClass}`}
                      style={{
                        fontSize: "0.85rem",
                        padding: "0.4rem 0.5rem",
                        backgroundColor: "transparent",
                        width: "100%",
                        transition: "background-color 0.2s",
                      }}
                      onFocus={(e) => {
                        e.target.style.backgroundColor = "#fff";
                        if (otherAmountModal.currentEntry.chargeType) {
                          getSuggestions(
                            otherAmountModal.currentEntry.chargeType
                          );
                        }
                      }}
                      onBlur={(e) => {
                        e.target.style.backgroundColor = "transparent";
                        setTimeout(() => setSuggestions([]), 200);
                      }}
                    />
                  </td>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <input
                      type="text"
                      value={otherAmountModal.currentEntry.pcs}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d*$/.test(value)) {
                          setOtherAmountModal((prev) => {
                            const updatedEntry = {
                              ...prev.currentEntry,
                              pcs: value,
                            };
                            // Recalculate amount if rate on is "per pcs"
                            if (
                              updatedEntry.rateOn?.toLowerCase() ===
                              "per pcs" &&
                              updatedEntry.rate
                            ) {
                              updatedEntry.amount =
                                calculateAmount(updatedEntry).toFixed(2);
                            }
                            return {
                              ...prev,
                              currentEntry: updatedEntry,
                            };
                          });
                        }
                      }}
                      className={`form-control border-0 ${buttonClass}`}
                      style={{
                        fontSize: "0.85rem",
                        padding: "0.4rem 0.5rem",
                        backgroundColor: "transparent",
                        width: "100%",
                        transition: "background-color 0.2s",
                        textAlign: "left",
                      }}
                      onFocus={(e) => (e.target.style.backgroundColor = "#fff")}
                      onBlur={(e) =>
                        (e.target.style.backgroundColor = "transparent")
                      }
                    />
                  </td>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <input
                      type="text"
                      value={otherAmountModal.currentEntry.ct}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d*\.?\d*$/.test(value)) {
                          setOtherAmountModal((prev) => {
                            const updatedEntry = {
                              ...prev.currentEntry,
                              ct: value,
                            };
                            // Recalculate amount if rate on is "per ct"
                            if (
                              updatedEntry.rateOn?.toLowerCase() === "per ct" &&
                              updatedEntry.rate
                            ) {
                              updatedEntry.amount =
                                calculateAmount(updatedEntry).toFixed(2);
                            }
                            return {
                              ...prev,
                              currentEntry: updatedEntry,
                            };
                          });
                        }
                      }}
                      className={`form-control border-0 ${buttonClass}`}
                      style={{
                        fontSize: "0.85rem",
                        padding: "0.4rem 0.5rem",
                        backgroundColor: "transparent",
                        width: "100%",
                        transition: "background-color 0.2s",
                        textAlign: "left",
                      }}
                      onFocus={(e) => (e.target.style.backgroundColor = "#fff")}
                      onBlur={(e) =>
                        (e.target.style.backgroundColor = "transparent")
                      }
                    />
                  </td>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <input
                      type="text"
                      value={otherAmountModal.currentEntry.wt}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d*\.?\d*$/.test(value)) {
                          setOtherAmountModal((prev) => {
                            const updatedEntry = {
                              ...prev.currentEntry,
                              wt: value,
                            };
                            // Recalculate amount if rate on is "per wt"
                            if (
                              updatedEntry.rateOn?.toLowerCase() === "per wt" &&
                              updatedEntry.rate
                            ) {
                              updatedEntry.amount =
                                calculateAmount(updatedEntry).toFixed(2);
                            }
                            return {
                              ...prev,
                              currentEntry: updatedEntry,
                            };
                          });
                        }
                      }}
                      className={`form-control border-0 ${buttonClass}`}
                      style={{
                        fontSize: "0.85rem",
                        padding: "0.4rem 0.5rem",
                        backgroundColor: "transparent",
                        width: "100%",
                        transition: "background-color 0.2s",
                        textAlign: "left",
                      }}
                      onFocus={(e) => (e.target.style.backgroundColor = "#fff")}
                      onBlur={(e) =>
                        (e.target.style.backgroundColor = "transparent")
                      }
                    />
                  </td>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <input
                      type="text"
                      value={otherAmountModal.currentEntry.conPercentage}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d*\.?\d*$/.test(value)) {
                          setOtherAmountModal((prev) => ({
                            ...prev,
                            currentEntry: {
                              ...prev.currentEntry,
                              conPercentage: value,
                            },
                          }));
                        }
                      }}
                      className={`form-control border-0 ${buttonClass}`}
                      style={{
                        fontSize: "0.85rem",
                        padding: "0.4rem 0.5rem",
                        backgroundColor: "transparent",
                        width: "100%",
                        transition: "background-color 0.2s",
                        textAlign: "left",
                      }}
                      onFocus={(e) => (e.target.style.backgroundColor = "#fff")}
                      onBlur={(e) =>
                        (e.target.style.backgroundColor = "transparent")
                      }
                    />
                  </td>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <div className="position-relative">
                      <input
                        type="text"
                        value={otherAmountModal.currentEntry.rateOn}
                        onChange={(e) => {
                          handleRateOnDropdown(null, e.target.value, true);
                          const dropdown = e.target.nextElementSibling;
                          if (dropdown) {
                            dropdown.style.display = "block";
                          }
                        }}
                        onKeyDown={(e) => handleRateOnKeyDown(e, null, true)}
                        className={`form-control border-0 ${buttonClass}`}
                        style={{
                          fontSize: "0.85rem",
                          padding: "0.4rem 0.5rem",
                          backgroundColor: "transparent",
                          width: "100%",
                          transition: "background-color 0.2s",
                        }}
                        onFocus={(e) => {
                          e.target.style.backgroundColor = "#fff";
                          const rect = e.target.getBoundingClientRect();
                          const dropdown = e.target.nextElementSibling;
                          if (dropdown) {
                            // Calculate max width based on content
                            const maxWidth = Math.max(
                              ...rateOnOptions.map((item) => {
                                const tempSpan = document.createElement("span");
                                tempSpan.style.fontSize = "0.85rem";
                                tempSpan.style.visibility = "hidden";
                                tempSpan.style.position = "absolute";
                                tempSpan.style.whiteSpace = "nowrap";
                                tempSpan.textContent = item;
                                document.body.appendChild(tempSpan);
                                const width = tempSpan.offsetWidth;
                                document.body.removeChild(tempSpan);
                                return width;
                              }),
                              rect.width // minimum width is input width
                            );

                            dropdown.style.display = "block";
                            dropdown.style.position = "fixed";
                            dropdown.style.top = `${rect.bottom}px`;
                            dropdown.style.left = `${rect.left}px`;
                            dropdown.style.width = "auto";
                            dropdown.style.minWidth = `${rect.width}px`;
                            dropdown.style.maxWidth = `${maxWidth + 32}px`; // Add padding
                            dropdown.style.zIndex = "9999";
                            // Reset highlighted index when opening dropdown
                            setHighlightedIndex(0);
                          }
                        }}
                        onBlur={(e) => {
                          e.target.style.backgroundColor = "transparent";
                          setTimeout(() => {
                            const dropdown = e.target.nextElementSibling;
                            if (dropdown) {
                              dropdown.style.display = "none";
                            }
                          }, 200);
                        }}
                      />
                      <div
                        className="dropdown-menu shadow-sm"
                        style={{
                          display: "none",
                          maxHeight: "200px",
                          overflowY: "auto",
                          backgroundColor: "#fff",
                          position: "fixed",
                          zIndex: 9999,
                          border: "1px solid rgba(0,0,0,.15)",
                          borderRadius: "0.25rem",
                          padding: "0.5rem 0",
                          width: "auto",
                          minWidth: "100%",
                        }}
                      >
                        {rateOnOptions
                          .filter((option) =>
                            option
                              .toLowerCase()
                              .includes(
                                (
                                  otherAmountModal.currentEntry.rateOn || ""
                                ).toLowerCase()
                              )
                          )
                          .map((option, i) => (
                            <div
                              key={i}
                              className={`dropdown-item ${i === highlightedIndex ? "active" : ""
                                }`}
                              style={{
                                cursor: "pointer",
                                padding: "0.25rem 1rem",
                                fontSize: "0.85rem",
                                backgroundColor:
                                  i === highlightedIndex
                                    ? "var(--theme-primary)"
                                    : option.toLowerCase() ===
                                      otherAmountModal.currentEntry.rateOn.toLowerCase()
                                      ? "#f0f0f0"
                                      : "transparent",
                                color:
                                  i === highlightedIndex ? "#fff" : "inherit",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              onMouseEnter={() => setHighlightedIndex(i)}
                              onMouseDown={() => {
                                handleRateOnDropdown(null, option, true);
                                // Hide dropdown after selection
                                const dropdown = document.querySelector(
                                  '.dropdown-menu[style*="display: block"]'
                                );
                                if (dropdown) {
                                  dropdown.style.display = "none";
                                }
                              }}
                            >
                              {option}
                            </div>
                          ))}
                      </div>
                    </div>
                  </td>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <input
                      type="text"
                      value={otherAmountModal.currentEntry.rate}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d*\.?\d*$/.test(value)) {
                          setOtherAmountModal((prev) => {
                            const updatedEntry = {
                              ...prev.currentEntry,
                              rate: value,
                            };
                            // Calculate amount whenever rate changes
                            if (value && updatedEntry.rateOn) {
                              updatedEntry.amount = formatAmount(
                                calculateAmount(updatedEntry)
                              );
                            } else {
                              updatedEntry.amount = "";
                            }
                            return {
                              ...prev,
                              currentEntry: updatedEntry,
                            };
                          });
                        }
                      }}
                      className={`form-control border-0 ${buttonClass}`}
                      style={{
                        fontSize: "0.85rem",
                        padding: "0.4rem 0.5rem",
                        backgroundColor: "transparent",
                        width: "100%",
                        transition: "background-color 0.2s",
                        textAlign: "left",
                      }}
                      onFocus={(e) => (e.target.style.backgroundColor = "#fff")}
                      onBlur={(e) =>
                        (e.target.style.backgroundColor = "transparent")
                      }
                    />
                  </td>
                  <td
                    className="p-0 position-relative"
                    style={{ border: "1px solid #dee2e6" }}
                  >
                    <input
                      type="text"
                      value={otherAmountModal.currentEntry.amount}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d*\.?\d*$/.test(value)) {
                          setOtherAmountModal((prev) => ({
                            ...prev,
                            currentEntry: {
                              ...prev.currentEntry,
                              amount: value,
                            },
                          }));
                        }
                      }}
                      onKeyDown={(e) => {
                        // Save and close modal on Enter if amount is valid
                        if (e.key === "Enter") {
                          const value = otherAmountModal.currentEntry.amount;
                          if (value && !isNaN(value) && Number(value) > 0) {
                            e.preventDefault();

                            // Add current entry to entries
                            const finalEntries = [
                              ...otherAmountModal.entries,
                              otherAmountModal.currentEntry,
                            ];

                            // Calculate total amount
                            const totalAmount = finalEntries.reduce(
                              (sum, entry) => sum + Number(entry.amount || 0),
                              0
                            );

                            // Calculate total less weight
                            const totalLessWeight = finalEntries.reduce(
                              (sum, entry) => {
                                const lessWeight = calculateLessWeight(entry);
                                return (
                                  sum + (isNaN(lessWeight) ? 0 : lessWeight)
                                );
                              },
                              0
                            );

                            // Update table data with both values
                            setTableData((prev) => {
                              const newData = [...prev];
                              const row = newData[otherAmountModal.rowIndex];

                              if (!row || !row.data) return prev;

                              // Update Other Amount (rounded)
                              const othAmtCell = row.data.find(
                                (cell) => cell.id === "othAmt"
                              );
                              if (othAmtCell) {
                                othAmtCell.value = formatAmount(totalAmount);
                                othAmtCell.otherAmountEntries = finalEntries;
                              }

                              // Update Less Weight (always show with 2 decimal places)
                              const lsWtCell = row.data.find(
                                (cell) => cell.id === "lsWt"
                              );
                              if (lsWtCell) {
                                lsWtCell.value =
                                  formatLessWeight(totalLessWeight);
                              }

                              return newData;
                            });

                            // Close modal
                            setOtherAmountModal((prev) => ({
                              ...prev,
                              isOpen: false,
                            }));
                          }
                        }
                      }}
                      onFocus={(e) => {
                        e.target.style.backgroundColor = "#fff";
                        // When focus reaches the amount field, add a new row in the background
                        setTimeout(() => {
                          setOtherAmountModal((prev) => {
                            // Only add new row if current row has any data
                            if (
                              prev.currentEntry.chargeType ||
                              prev.currentEntry.pcs ||
                              prev.currentEntry.ct ||
                              prev.currentEntry.wt ||
                              prev.currentEntry.conPercentage ||
                              prev.currentEntry.rateOn ||
                              prev.currentEntry.rate
                            ) {
                              return {
                                ...prev,
                                entries: [...prev.entries],
                                currentEntry: { ...prev.currentEntry },
                              };
                            }
                            return prev;
                          });
                        }, 0);
                      }}
                      onBlur={(e) => {
                        e.target.style.backgroundColor = "transparent";
                        // Move to next row on blur if amount is valid
                        const value = otherAmountModal.currentEntry.amount;
                        if (value && !isNaN(value) && Number(value) > 0) {
                          // Add current entry and move to next row
                          setOtherAmountModal((prev) => ({
                            ...prev,
                            entries: [
                              ...prev.entries,
                              { ...prev.currentEntry },
                            ],
                            currentEntry: {
                              chargeType: "",
                              pcs: "",
                              ct: "",
                              wt: "",
                              conPercentage: "",
                              rateOn: "",
                              rate: "",
                              amount: "",
                            },
                          }));
                          // Focus on first cell of next row
                          setTimeout(() => {
                            const firstInput = document.querySelector(
                              ".modal-body tbody tr:last-child td:nth-child(2) input"
                            );
                            if (firstInput) {
                              firstInput.focus();
                            }
                          }, 0);
                        }
                      }}
                      className={`form-control border-0 ${buttonClass}`}
                      style={{
                        fontSize: "0.85rem",
                        padding: "0.4rem 0.5rem",
                        backgroundColor: "transparent",
                        width: "100%",
                        transition: "background-color 0.2s",
                        textAlign: "left",
                      }}
                    />
                  </td>
                </tr>
              </tbody>
              <tfoot>{/* Remove the total row from table */}</tfoot>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-4">
            <div className="d-flex align-items-center gap-2">
              <span className="text-dark" style={{ fontSize: "0.9rem" }}>
                LWt:
              </span>
              <span className="fw-bold" style={{ fontSize: "0.9rem" }}>
                {formatLessWeight(
                  otherAmountModal.entries.reduce((sum, entry) => {
                    const lessWeight = calculateLessWeight(entry);
                    return sum + (isNaN(lessWeight) ? 0 : lessWeight);
                  }, 0)
                )}
              </span>
            </div>
            <div
              className="border-end h-100 mx-2"
              style={{ width: "1px", backgroundColor: "#dee2e6" }}
            ></div>
            <div className="d-flex align-items-center gap-2">
              <span className="text-dark" style={{ fontSize: "0.9rem" }}>
                Total:
              </span>
              <span className="fw-bold" style={{ fontSize: "0.9rem" }}>
                ₹ {formatAmount(calculateTotal(otherAmountModal.entries))}
              </span>
            </div>
          </div>
          <Button
            variant="primary"
            className={buttonClass}
            onClick={handleSaveOtherAmountEntries}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  // Add missing functions
  const handleSaveOtherAmountEntries = () => {
    // This function is referenced but not implemented
  };

  const handleOtherAmountColumnWidthChange = (column, newWidth) => {
    setOtherAmountColumnWidths((prev) => ({
      ...prev,
      [column]: `${newWidth}px`,
    }));
  };

  // Add this function right before the return statement in the Table component
  // Function to position tooltip based on screen size
  const positionTooltip = (targetElement, tooltip) => {
    const rect = targetElement.getBoundingClientRect();
    tooltip.style.display = "block";

    // Get arrow element
    const arrow = tooltip.querySelector(".tooltip-arrow");

    // Responsive positioning based on viewport
    const viewportWidth = window.innerWidth;

    if (viewportWidth < 576) {
      // Small screens (Bootstrap's sm breakpoint)
      // Position directly below the "i" button
      tooltip.style.left = `${rect.left + rect.width / 2 - 100}px`; // Center horizontally (tooltip width is 200px)
      tooltip.style.top = `${rect.bottom + 10}px`; // Position below the button
      tooltip.style.maxWidth = "calc(100vw - 20px)"; // 10px padding on each side

      // Position arrow at top center
      if (arrow) {
        arrow.style.top = "-6px"; // Adjusted for better alignment
        arrow.style.left = "50%";
        arrow.style.transform = "translateX(-50%) rotate(45deg)";
        arrow.style.borderRight = "none";
        arrow.style.borderBottom = "none";
        arrow.style.borderLeft = "1px solid rgba(0,0,0,0.1)";
        arrow.style.borderTop = "1px solid rgba(0,0,0,0.1)";
      }
    } else {
      // Position to the right on larger screens
      tooltip.style.left = `${rect.right + 10}px`;
      tooltip.style.top = `${rect.top - 5}px`;
      tooltip.style.maxWidth = "300px";

      // Position arrow at left middle
      if (arrow) {
        arrow.style.top = "15px";
        arrow.style.left = "-6px";
        arrow.style.transform = "rotate(45deg)";
        arrow.style.borderRight = "none";
        arrow.style.borderBottom = "none";
      }
    }
  };

  // Update the Rate On dropdown implementation
  const handleRateOnDropdown = (rowIndex, value, isCurrentEntry = false) => {
    if (isCurrentEntry) {
      setOtherAmountModal((prev) => {
        const updatedEntry = {
          ...prev.currentEntry,
          rateOn: value,
        };
        // Calculate amount when rate on changes
        if (updatedEntry.rate) {
          updatedEntry.amount = formatAmount(calculateAmount(updatedEntry));
        }
        return {
          ...prev,
          currentEntry: updatedEntry,
        };
      });
    } else {
      setOtherAmountModal((prev) => {
        const newEntries = [...prev.entries];
        const updatedEntry = {
          ...newEntries[rowIndex],
          rateOn: value,
        };
        // Calculate amount when rate on changes
        if (updatedEntry.rate) {
          updatedEntry.amount = formatAmount(calculateAmount(updatedEntry));
        }
        newEntries[rowIndex] = updatedEntry;
        return {
          ...prev,
          entries: newEntries,
        };
      });
    }

    // Hide the dropdown after selection
    const dropdowns = document.querySelectorAll(".dropdown-menu");
    dropdowns.forEach((dropdown) => {
      if (dropdown.style.display === "block") {
        dropdown.style.display = "none";
      }
    });
  };

  // Add keyboard navigation for Rate On dropdown
  const handleRateOnKeyDown = (e, rowIndex, isCurrentEntry = false) => {
    const currentValue = isCurrentEntry
      ? otherAmountModal.currentEntry.rateOn
      : otherAmountModal.entries[rowIndex].rateOn;

    const filtered = rateOnOptions.filter((option) =>
      option.toLowerCase().includes((currentValue || "").toLowerCase())
    );

    if (filtered.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filtered.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case "Enter":
        e.preventDefault();
        if (filtered[highlightedIndex]) {
          handleRateOnDropdown(
            rowIndex,
            filtered[highlightedIndex],
            isCurrentEntry
          );
        }
        break;
      case "Escape":
        e.preventDefault();
        // Close dropdown
        const dropdown = e.target.nextElementSibling;
        if (dropdown) {
          dropdown.style.display = "none";
        }
        break;
      default:
        break;
    }
  };

  // Add calculation functions before the Table component
  const calculateTotalCt = (entry) => {
    const ct = parseFloat(entry.ct);

    if (isNaN(ct)) return 0;
    return ct;
  };

  const calculateTotalWt = (entry) => {
    const pcs = parseFloat(entry.pcs);
    const wt = parseFloat(entry.wt);

    if (isNaN(pcs) || isNaN(wt)) return 0;
    return pcs * wt;
  };

  const calculateLessWeight = (entry) => {
    // Calculate total weight first
    const totalWt = calculateTotalWt(entry);
    if (!totalWt || isNaN(totalWt)) return 0;

    // Get concentration percentage
    const conPercentage = parseFloat(entry.conPercentage);

    // If there's a valid concentration percentage, calculate less weight based on that
    if (!isNaN(conPercentage) && conPercentage > 0) {
      return totalWt * (conPercentage / 100);
    }

    // If no concentration percentage, return the total weight
    return totalWt;
  };

  const calculateAmount = (entry) => {
    if (!entry.rate || isNaN(entry.rate)) return 0;

    const rate = parseFloat(entry.rate);
    const rateOn = entry.rateOn?.toLowerCase() || "";
    let amount = 0;

    switch (rateOn) {
      case "fixed":
        amount = rate;
        break;
      case "pcs":
        amount =
          entry.pcs && !isNaN(entry.pcs) ? rate * parseFloat(entry.pcs) : 0;
        break;
      case "ct":
        const totalCt = calculateTotalCt(entry);
        amount = totalCt ? rate * totalCt : 0;
        break;
      case "wt":
        const totalWt = calculateTotalWt(entry);
        amount = totalWt ? rate * totalWt : 0;
        break;
      default:
        amount = 0;
    }

    // Return the formatted amount
    return amount;
  };

  const calculateTotal = (entries) => {
    return entries.reduce(
      (sum, entry) => sum + (parseFloat(entry.amount) || 0),
      0
    );
  };

  // Add a helper function for number formatting
  const formatAmount = (value) => {
    if (!value || isNaN(value)) return "";
    // Convert to number and handle rounding based on decimal part
    const num = parseFloat(value);
    const decimalPart = num - Math.floor(num);
    // If decimal part is >= 0.5, round up, otherwise round down
    return decimalPart >= 0.5
      ? Math.ceil(num).toString()
      : Math.floor(num).toString();
  };

  // Update formatLessWeight to always return a value with 2 decimal places
  const formatLessWeight = (value) => {
    // Convert to number first
    const num = parseFloat(value);
    // Check if it's a valid number (including 0)
    if (isNaN(num)) return "0.00";
    // Always return with 2 decimal places
    return num.toFixed(2);
  };

  // Define product and setProductGroups using useState

  const [showSuccessModal, setShowSuccessModal] = useState(false); // State for modal visibility
  const [lastHuidEnterPress, setLastHuidEnterPress] = useState({
    time: 0,
    rowIndex: null,
  });
  const [huidModal, setHuidModal] = useState({ isOpen: false, rowIndex: null });
  const [otherAmountModal, setOtherAmountModal] = useState({
    isOpen: false,
    rowIndex: null,
    entries: [],
    currentEntry: {
      chargeType: "",
      pcs: "",
      ct: "",
      wt: "",
      conPercentage: "",
      rateOn: "",
      rate: "",
      amount: "",
    },
    editingIndex: undefined,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate mandatory fields
    if (!orderType) {
      toast.error("Type is required!");
      return;
    }

    if (!selectedMobile) {
      toast.error("Select Mobile is required!");
      return;
    }

    if (!visitorNames || visitorNames.trim() === "") {
      toast.error("Visitor Names is required!");
      return;
    }

    // We now rely solely on the ID captured when selecting from the dropdown
    const resolvedPersonId = selectedPersonId;
    if (!resolvedPersonId) {
      toast.error(
        `Please select a valid ${orderType === "Supplier/Karigar" ? "Supplier or Karigar" : orderType
        } from the dropdown!`
      );
      return;
    }

    // Determine the group_type for submission
    // If orderType is "Supplier/Karigar", use selectedPersonType; otherwise use orderType
    const resolvedGroupType =
      orderType === "Supplier/Karigar"
        ? selectedPersonType || "Supplier"
        : orderType;

    // Create an array to hold all jangad items
    const jangadItems = [];

    // Process each row in the table
    for (let rowIndex = 0; rowIndex < tableData.length; rowIndex++) {
      const rowData = tableData[rowIndex].data.reduce((acc, cell) => {
        acc[cell.id] = cell.value;
        return acc;
      }, {});

      // Skip rows with empty required fields
      if (!rowData.productGroup || !rowData.pcs || !rowData.grWt) {
        continue;
      }

      const productGroupCell = tableData[rowIndex].data.find(
        (cell) => cell.id === "productGroup"
      );
      const selectedProductGroup = dropdownData.productGroup.find(
        (group) =>
          group.label === rowData.productGroup ||
          group.short_code === rowData.productGroup ||
          String(group.value) === String(productGroupCell?.productGroupId || "")
      );

      // Get LBR amount
      let lbrAmount = 0;
      const lbrAmtCell = tableData[rowIndex].data.find(
        (cell) => cell.id === "lbrAmt"
      );
      if (lbrAmtCell && lbrAmtCell.value) {
        lbrAmount = Number(lbrAmtCell.value) || 0;
      }

      // Get user data for by_user field
      const userData = getUserData();

      const jangadItem = {
        // by_user: userData?.id || null,
        product_tag_no: rowData.tagNo || "",
        product_group:
          selectedProductGroup?.value ??
          productGroupCell?.productGroupId ??
          rowData.productGroup,
        pcs: Number(rowData.pcs) || 0,
        gwt: Number(rowData.grWt) || 0,
        nwt: Number(rowData.netWt) || 0,
        lwt: Number(rowData.lsWt) || 0,
        item_remarks: rowData.remarks || "",
      };

      jangadItems.push(jangadItem);
    }
    // console.log(jangadItems);
    // Check if we have any valid entries
    if (jangadItems.length === 0) {
      toast.error(
        "No valid jangad entries to submit. Please complete at least one row with Product Group, Pcs, and GWt."
      );
      return;
    }

    try {
      // Build modular ERP payload (plain JSON, idempotency key per action)
      const party_type =
        String(resolvedGroupType || "")
          .toLowerCase()
          .includes("customer")
          ? "CUSTOMER"
          : "SUPPLIER";

      const visitors = createVisitorsPayload({
        visitorNames,
        visitorMobiles,
        noOfVisitors,
      });

      if (!party_type || !resolvedPersonId) {
        toast.error("Invalid party for jangad creation");
        return;
      }

      if (!Array.isArray(jangadItems) || jangadItems.length === 0) {
        toast.error("No jangad items to submit");
        return;
      }

      // Resolve product_id from tag_no (API requires product_id).
      const apiItems = await Promise.all(
        jangadItems.map(async (it) => {
          const tagNo = it.product_tag_no || it.tag_no || "";
          const productRes = await showProducts({ tag_no: tagNo });
          const normalizedProducts = normalizeProductStockShowResponse(productRes);
          const product =
            (Array.isArray(normalizedProducts) && normalizedProducts.length > 0
              ? normalizedProducts[0]
              : null) ??
            productRes?.data?.product ??
            productRes?.data?.data?.product ??
            null;

          if (!product?.id) {
            throw new Error(
              `Unable to resolve product for tag_no: ${tagNo}`
            );
          }

          const gwt = Number(it.gwt ?? 0) || 0;
          const nwt = Number(it.nwt ?? 0) || 0;
          const lwt = Number(it.lwt ?? 0) || 0;
          const weight = Number(it.nwt ?? it.gwt ?? 0) || 0;
          return {
            product_id: product.id,
            pcs: Number(it.pcs ?? 0) || 0,
            item_remarks: it.item_remarks ?? "",
            // Keep `weight` for backward-compatibility, but also send explicit fields
            weight,
            gwt,
            nwt,
            lwt,
          };
        }),
      );

      const basePayload = {
        party_type,
        party_id: resolvedPersonId,
        date: billDate,
        expected_return: billDate,
        remarks,
        reference: reference ?? "",
        visitors,
        items: apiItems,
      };

      const fingerprint = JSON.stringify(basePayload);

      if (
        !jangadCreateIdempotencyKeyRef.current ||
        jangadCreatePayloadFingerprintRef.current !== fingerprint
      ) {
        jangadCreateIdempotencyKeyRef.current = createJangadIdempotencyKey();
        jangadCreatePayloadFingerprintRef.current = fingerprint;
      }

      const response = await createJangad({
        ...basePayload,
        idempotency_key: jangadCreateIdempotencyKeyRef.current,
      });

      if (isJangadsApiSuccess(response)) {
        toast.success("Jangad added successfully!");
        setShowSuccessModal(true);

        setTimeout(() => setShowSuccessModal(false), 2000);
        resetForm();

        // Refresh by calling show() (best-effort; UI navigates anyway)
        const jangad_document_id =
          response?.jangad_document_id ?? response?.data?.jangad_document_id;
        if (jangad_document_id) {
          try {
            await showJangad({ id: jangad_document_id });
          } catch {
            // ignore refresh failure; navigation still succeeds
          }
        }

        // Navigate to Jangad page after successful submission
        navigate("/Jangad");
      } else {
        toast.error(response?.message || "Failed to add jangad");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to add jangad");
    }
  };

  // Updated handleImageUpload function - exactly like profile code
  // Updated handleImageUpload - Only store the file, don't upload yet
  const handleImageUpload = async (field, rowIndex) => {
    // Create a preview of the image
    const fileReader = new FileReader();
    fileReader.onload = async (e) => {
      // Update the image preview and store the file in the table data
      setTableData((prevData) => {
        const updatedData = [...prevData];
        if (updatedData[rowIndex] && updatedData[rowIndex].data) {
          const imageCell = updatedData[rowIndex].data.find(
            (cell) => cell.id === "image"
          );
          if (imageCell) {
            imageCell.imagePreview = e.target.result;
            imageCell.imageFile = field; // Store the actual file object
            imageCell.value = null; // Clear any previous uploaded image name
          }
        }
        return updatedData;
      });
    };
    fileReader.readAsDataURL(field);
  };

  // Function to reset the form after successful submission
  const resetForm = () => {
    jangadCreateIdempotencyKeyRef.current = null;
    jangadCreatePayloadFingerprintRef.current = null;

    // Reset the table data to a single empty row
    const initialRowData = COLUMNS.map((col) => ({
      id: col.id,
      value: "",
      ...(col.id === "image" ? { imageFile: null, imagePreview: null } : {}),
    }));
    setTableData([{ id: 1, data: initialRowData }]);

    // Reset any active dropdowns or modals
    setActiveDropdown({ row: null, col: null, type: null });
    setOtherAmountModal({
      isOpen: false,
      rowIndex: null,
      entries: [],
      currentEntry: {
        chargeType: "",
        pcs: "",
        ct: "",
        wt: "",
        conPercentage: "",
        rateOn: "",
        rate: "",
        amount: "",
      },
      editingIndex: undefined,
    });

    // Set today's date
    const today = new Date().toISOString().split("T")[0];
    setBillDate(today);

    // Reset order type and selected mobile/name
    setOrderType("Customer");
    setSelectedMobile("");
    setSelectedName("");
    setSelectedPersonId(null);
    setSelectedPersonType(null);
    setShowMobileDropdown(false);

    // Reset visitor fields
    setNoOfVisitors(1);
    setVisitorNames("");
    setVisitorMobiles("");
    setReference("");
    setRemarks("");

    // Reset image modal if open
    setImageModal({
      isOpen: false,
      imageUrl: null,
      zoomLevel: 1,
    });

    // Reset any input focus
    document.activeElement?.blur();

    // Reset form to top position
    window.scrollTo(0, 0);
  };

  const openHuidModal = (rowIndex) => {
    setHuidModal({ isOpen: true, rowIndex });
  };

  const closeHuidModal = () => {
    setHuidModal({ isOpen: false, rowIndex: null });
  };

  const handleHuidKeyDown = (e, rowIndex) => {
    if (e.key === "Enter") {
      const now = Date.now();
      if (
        lastHuidEnterPress.rowIndex === rowIndex &&
        now - lastHuidEnterPress.time < 500
      ) {
        // 500ms threshold for double-Enter
        e.preventDefault();
        openHuidModal(rowIndex);
        setLastHuidEnterPress({ time: 0, rowIndex: null });
      } else {
        setLastHuidEnterPress({ time: now, rowIndex });
      }
    }
  };

  const renderHuidModal = () => {
    if (!huidModal.isOpen) return null;

    return (
      <Modal show={huidModal.isOpen} onHide={closeHuidModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>HUID Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">HUID Number</label>
            <textarea
              className={`form-control ${buttonClass}`}
              rows="3"
              style={{ resize: "vertical" }}
              value={
                tableData[huidModal.rowIndex]?.data.find(
                  (cell) => cell.id === "huidNo"
                )?.value || ""
              }
              onChange={(e) => {
                handleInputChange(
                  huidModal.rowIndex,
                  tableData[huidModal.rowIndex]?.data.findIndex(
                    (cell) => cell.id === "huidNo"
                  ),
                  e.target.value
                );
              }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            className={buttonClass}
            onClick={closeHuidModal}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  return (
    <div className={`body-wrapper ${cardClass}`}>
      <div className="container-fluid">
        <div className={`card shadow-sm ${cardClass}`}>
          <div className="px-4 py-3 border-bottom">
            <h4 className="card-title mb-0">Add Jangad</h4>
          </div>
          <div className="card-body">
            <div className="row mb-1">
              {/* Exp Delivery */}
              <div className="col-lg-3 col-md-4 col-sm-6 mb-2">
                <div className="d-flex align-items-center">
                  <label
                    className="form-label me-2 mb-0 fw-semibold text-nowrap"
                    style={{ minWidth: "90px" }}
                  >
                    Exp Delivery:
                  </label>
                  <input
                    type="date"
                    className={`form-control form-control-sm ${buttonClass}`}
                    value={billDate}
                    onChange={handleDateChange}
                  />
                </div>
              </div>

              {/* Order Type Dropdown */}
              <div className="col-lg-3 col-md-4 col-sm-6 mb-2">
                <div className="d-flex align-items-center">
                  <label
                    className="form-label me-2 mb-0 fw-semibold"
                    style={{ width: "80px" }}
                  >
                    Type:
                  </label>
                  <select
                    className={`form-select form-select-sm ${buttonClass}`}
                    value={orderType}
                    onChange={handleOrderTypeChange}
                  >
                    <option value="Customer">Customer</option>
                    <option value="Supplier/Karigar">Supplier/Karigar</option>
                  </select>
                </div>
              </div>

              {/* Name (search field, 3+ chars for dropdown) */}
              <div className="col-lg-3 col-md-5 col-sm-6 mb-2">
                <div className="d-flex align-items-center position-relative">
                  <label
                    className="form-label me-1 mb-0 fw-semibold text-nowrap"
                    style={{ minWidth: "55px" }}
                  >
                    Name: <span className="text-danger">*</span>
                  </label>
                  <div className="position-relative flex-grow-1">
                    <input
                      ref={mobileInputRef} // anchor dropdown to name input
                      type="text"
                      className={`form-control form-control-sm ${buttonClass}`}
                      placeholder="Name"
                      value={selectedName}
                      onChange={handleMobileInputChange}
                      onFocus={handleMobileFocus}
                      onBlur={handleMobileBlur}
                      onKeyDown={handleMobileKeyDown}
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>

              {/* Mobile display */}
              <div className="col-lg-2 col-md-7 col-sm-12 mb-2">
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="text"
                    className={`form-control form-control-sm ${buttonClass}`}
                    placeholder="Mobile No"
                    value={selectedMobile}
                    readOnly
                  />
                </div>
              </div>
            </div>

            <div
              className={`table-responsive border mt-3 ${cardClass}`}
              style={{
                maxHeight: "calc(38px * 7)", // Height for 7 rows (38px per row)
                overflowY: "auto",
                position: "relative",
              }}
            >
              <table
                className={`table table-bordered table-hover table-sm mb-0 theme-header-table ${tableClass}`}
              >
                <thead
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  <tr>
                    {COLUMNS.map((column) => (
                      <th
                        key={column.id}
                        className="px-2 py-2 text-center"
                        style={{
                          fontSize: "0.85rem",
                          width: column.width,
                          fontWeight: "600",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, rowIndex) => (
                    <tr key={row.id} style={{ backgroundColor: "#ffffff" }}>
                      {row.data.map((cell, colIndex) => {
                        // Add special handling for serial number column
                        if (cell.id === "no") {
                          return (
                            <td
                              key={cell.id}
                              className="p-0"
                              style={{ width: COLUMNS[colIndex]?.width }}
                            >
                              <div
                                className="d-flex align-items-center justify-content-center"
                                style={{ height: "38px" }}
                              >
                                <span style={{ fontSize: "0.85rem" }}>
                                  {rowIndex + 1}
                                </span>
                              </div>
                            </td>
                          );
                        }

                        // Column handling simplified

                        return (
                          <td
                            key={cell.id}
                            className="p-0"
                            style={{
                              width: COLUMNS[colIndex]?.width,
                              position: "relative",
                            }}
                          >
                            {isDropdownColumn(getColumnType(colIndex)) ? (
                              // Dropdown-enabled cell
                              <div className="position-relative">
                                <input
                                  ref={(el) =>
                                  (inputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    handleInputChange(
                                      rowIndex,
                                      colIndex,
                                      newValue
                                    );
                                    // Show dropdown when typing, but for productGroup only after 3+ characters
                                    const columnType = getColumnType(colIndex);

                                    // Check if this is a column that requires 3+ characters
                                    const requiresMinLength =
                                      columnType === "productGroup" ||
                                      columnType === "tagNo";
                                    const meetsMinLength =
                                      !requiresMinLength ||
                                      newValue.length >= 3;

                                    // Only show dropdown if minimum length is met (or not required)
                                    if (meetsMinLength) {
                                      setTimeout(() => {
                                        handleDropdownClick(
                                          rowIndex,
                                          colIndex,
                                          columnType
                                        );
                                      }, 0);
                                    } else {
                                      // Close dropdown if less than 3 characters
                                      setActiveDropdown({
                                        row: null,
                                        col: null,
                                        type: null,
                                      });
                                    }
                                  }}
                                  onKeyDown={(e) =>
                                    handleKeyDown(
                                      e,
                                      rowIndex,
                                      colIndex,
                                      getColumnType(colIndex)
                                    )
                                  }
                                  onClick={() => {
                                    const columnType = getColumnType(colIndex);
                                    // For productGroup and tagNo, only show dropdown if 3+ characters
                                    if (
                                      columnType === "productGroup" ||
                                      columnType === "tagNo"
                                    ) {
                                      if (
                                        cell.value &&
                                        cell.value.length >= 3
                                      ) {
                                        handleDropdownClick(
                                          rowIndex,
                                          colIndex,
                                          columnType
                                        );
                                      }
                                    } else {
                                      // For other columns, show dropdown normally
                                      handleDropdownClick(
                                        rowIndex,
                                        colIndex,
                                        columnType
                                      );
                                    }
                                  }}
                                  className={`form-control border-0 ${buttonClass}`}
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                  }}
                                  onFocus={() =>
                                    handleFocus(rowIndex, colIndex)
                                  }
                                  onBlur={(e) => {
                                    e.target.style.backgroundColor =
                                      "transparent";
                                  }}
                                  autoComplete="off"
                                />
                              </div>
                            ) : isImageColumn(getColumnType(colIndex)) ? (
                              <div
                                className="position-relative d-flex align-items-center justify-content-center"
                                style={{ height: "38px" }}
                              >
                                <input
                                  type="file"
                                  accept="image/*"
                                  ref={(el) =>
                                  (fileInputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                  }
                                  style={{ display: "none" }}
                                  onChange={(e) =>
                                    handleImageUpload(
                                      e.target.files[0],
                                      rowIndex
                                    )
                                  }
                                />

                                {cell.imagePreview ? (
                                  <div
                                    className="position-relative"
                                    style={{ height: "38px" }}
                                  >
                                    <div
                                      className="d-flex justify-content-center align-items-center h-100"
                                      onMouseEnter={(e) => {
                                        // Clear any existing timeout
                                        if (tooltipTimeoutRef.current) {
                                          clearTimeout(
                                            tooltipTimeoutRef.current
                                          );
                                          tooltipTimeoutRef.current = null;
                                        }

                                        // Hide all tooltips first
                                        Object.values(
                                          tooltipRefs.current
                                        ).forEach((tooltip) => {
                                          if (tooltip)
                                            tooltip.style.display = "none";
                                        });

                                        const tooltipId = `tooltip-${rowIndex}-${colIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // Get the position of the image
                                          const rect =
                                            e.currentTarget.getBoundingClientRect();

                                          // Position the tooltip centered below the image
                                          tooltip.style.position = "fixed";
                                          tooltip.style.left =
                                            rect.left +
                                            rect.width / 2 -
                                            100 +
                                            "px"; // Center horizontally (tooltip width is 200px)
                                          tooltip.style.top =
                                            rect.bottom + 10 + "px"; // Position below the image
                                          tooltip.style.display = "block";

                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        // Set a timeout before hiding the tooltip
                                        tooltipTimeoutRef.current = setTimeout(
                                          () => {
                                            // Only hide if this is still the active tooltip
                                            const tooltipId = `tooltip-${rowIndex}-${colIndex}`;
                                            if (activeTooltip === tooltipId) {
                                              const tooltip =
                                                tooltipRefs.current[tooltipId];
                                              if (tooltip) {
                                                tooltip.style.display = "none";
                                              }
                                              setActiveTooltip(null);
                                            }
                                          },
                                          300
                                        ); // Delay to allow moving to tooltip
                                      }}
                                    >
                                      <img
                                        src={cell.imagePreview}
                                        alt="Preview"
                                        className="img-thumbnail"
                                        style={{
                                          height: "30px",
                                          width: "30px",
                                          objectFit: "cover",
                                          cursor: "pointer",
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openImageModal(cell.imagePreview);
                                        }}
                                      />
                                    </div>

                                    {/* Hover preview tooltip */}
                                    <div
                                      ref={(el) =>
                                      (tooltipRefs.current[
                                        `tooltip-${rowIndex}-${colIndex}`
                                      ] = el)
                                      }
                                      className={`shadow p-3 bg-white ${buttonClass}`}
                                      style={{
                                        display: "none",
                                        width: "200px",
                                        zIndex: 9999,
                                        position: "fixed", // Use fixed positioning to escape table constraints
                                      }}
                                      onMouseEnter={(e) => {
                                        // Clear any existing timeout when entering the tooltip
                                        if (tooltipTimeoutRef.current) {
                                          clearTimeout(
                                            tooltipTimeoutRef.current
                                          );
                                          tooltipTimeoutRef.current = null;
                                        }

                                        // Make sure this is the only visible tooltip
                                        const currentTooltipId = `tooltip-${rowIndex}-${colIndex}`;
                                        Object.entries(
                                          tooltipRefs.current
                                        ).forEach(([id, tooltip]) => {
                                          if (
                                            tooltip &&
                                            id !== currentTooltipId
                                          ) {
                                            tooltip.style.display = "none";
                                          }
                                        });

                                        e.currentTarget.style.display = "block";
                                        setActiveTooltip(currentTooltipId);
                                      }}
                                      onMouseLeave={(e) => {
                                        // Hide immediately when leaving the tooltip itself
                                        e.currentTarget.style.display = "none";
                                        setActiveTooltip(null);
                                      }}
                                    >
                                      <div
                                        style={{
                                          position: "absolute",
                                          top: "-8px",
                                          left: "50%",
                                          transform: "translateX(-50%)",
                                          width: "16px",
                                          height: "8px",
                                          overflow: "hidden",
                                        }}
                                      >
                                        <div
                                          className="bg-white border"
                                          style={{
                                            position: "absolute",
                                            top: "0",
                                            left: "0",
                                            width: "16px",
                                            height: "16px",
                                            transform: "rotate(45deg)",
                                            boxShadow:
                                              "-2px -2px 2px rgba(0,0,0,0.1)",
                                            borderRight: "none",
                                            borderBottom: "none",
                                          }}
                                        ></div>
                                      </div>
                                      <div className="text-center mb-3">
                                        <img
                                          src={cell.imagePreview}
                                          alt="Preview"
                                          className={`img-fluid ${buttonClass}`}
                                          style={{
                                            maxHeight: "100px",
                                            objectFit: "contain",
                                          }}
                                        />
                                      </div>
                                      <div className="d-flex justify-content-center gap-2">
                                        <Button
                                          variant="primary"
                                          size="sm"
                                          className={buttonClass}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            triggerFileInput(
                                              rowIndex,
                                              colIndex
                                            );
                                          }}
                                          title="Change Image"
                                        >
                                          <Icon
                                            icon="solar:gallery-edit-bold"
                                            width="16"
                                            height="16"
                                          />
                                        </Button>
                                        <Button
                                          variant="danger"
                                          size="sm"
                                          className={buttonClass}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setTableData((prevData) => {
                                              const updatedData = [...prevData];
                                              const imageCell = updatedData[
                                                rowIndex
                                              ].data.find(
                                                (c) => c.id === "image"
                                              );
                                              if (imageCell) {
                                                imageCell.value = "";
                                                imageCell.imageFile = null;
                                                imageCell.imagePreview = null;
                                              }
                                              return updatedData;
                                            });
                                          }}
                                          title="Remove Image"
                                        >
                                          <Icon
                                            icon="solar:trash-bin-trash-bold"
                                            width="16"
                                            height="16"
                                          />
                                        </Button>
                                        <Button
                                          variant="primary"
                                          size="sm"
                                          className={buttonClass}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openImageModal(cell.imagePreview);
                                          }}
                                          title="View Image"
                                        >
                                          <Icon
                                            icon="solar:eye-bold"
                                            width="16"
                                            height="16"
                                          />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    className={buttonClass}
                                    onClick={() =>
                                      triggerFileInput(rowIndex, colIndex)
                                    }
                                    title="Upload Image"
                                  >
                                    <Icon
                                      icon="solar:gallery-add-bold"
                                      width="16"
                                      height="16"
                                    />
                                  </Button>
                                )}
                              </div>
                            ) : getColumnType(colIndex) === "totalamt" ? (
                              <div className="d-flex align-items-center position-relative">
                                <input
                                  ref={(el) =>
                                  (inputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    // Only allow changes if there are no entries
                                    if (!cell.otherAmountEntries?.length) {
                                      const value = e.target.value;
                                      if (
                                        value === "" ||
                                        /^\d*\.?\d*$/.test(value)
                                      ) {
                                        handleInputChange(
                                          rowIndex,
                                          colIndex,
                                          value
                                        );
                                      }
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    handleKeyDown(
                                      e,
                                      rowIndex,
                                      colIndex,
                                      getColumnType(colIndex)
                                    );
                                    handleOtherAmountInteraction(rowIndex, e);
                                  }}
                                  onDoubleClick={(e) =>
                                    handleOtherAmountInteraction(rowIndex, e)
                                  }
                                  className={`form-control border-0 ${buttonClass}`}
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                    cursor: "pointer",
                                  }}
                                  onFocus={() =>
                                    handleFocus(rowIndex, colIndex)
                                  }
                                  onBlur={(e) =>
                                  (e.target.style.backgroundColor =
                                    "transparent")
                                  }
                                  title={
                                    cell.otherAmountEntries?.length > 0
                                      ? "Double-click or press Enter twice to edit details"
                                      : "Enter amount directly or double-click/press Enter twice for details"
                                  }
                                />
                                {cell.otherAmountEntries?.length > 0 && (
                                  <div className="position-relative">
                                    <Button
                                      variant="link"
                                      className="p-0 px-1 text-primary"
                                      style={{
                                        fontSize: "1rem",
                                        position: "absolute",
                                        right: "5px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        zIndex: 1,
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // If tooltip is already visible, hide it
                                          if (
                                            tooltip.style.display === "block"
                                          ) {
                                            tooltip.style.display = "none";
                                            setActiveTooltip(null);

                                            // Clear any existing timeout
                                            if (tooltipTimeoutRef.current) {
                                              clearTimeout(
                                                tooltipTimeoutRef.current
                                              );
                                              tooltipTimeoutRef.current = null;
                                            }
                                            return;
                                          }

                                          // Hide any other visible tooltips first
                                          Object.entries(
                                            tooltipRefs.current
                                          ).forEach(([id, t]) => {
                                            if (t && id !== tooltipId) {
                                              t.style.display = "none";
                                            }
                                          });

                                          // Position and show tooltip
                                          positionTooltip(
                                            e.currentTarget,
                                            tooltip
                                          );

                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);

                                          // Set timeout to hide after 4 seconds
                                          if (tooltipTimeoutRef.current) {
                                            clearTimeout(
                                              tooltipTimeoutRef.current
                                            );
                                          }

                                          tooltipTimeoutRef.current =
                                            setTimeout(() => {
                                              if (tooltip) {
                                                tooltip.style.display = "none";
                                                setActiveTooltip(null);
                                                tooltipTimeoutRef.current =
                                                  null;
                                              }
                                            }, 4000);
                                        }
                                      }}
                                      onMouseEnter={(e) => {
                                        // Don't show on hover if already showing from a click
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // Hide any other visible tooltips first
                                          Object.entries(
                                            tooltipRefs.current
                                          ).forEach(([id, t]) => {
                                            if (t && id !== tooltipId) {
                                              t.style.display = "none";
                                            }
                                          });

                                          // Position and show tooltip
                                          positionTooltip(
                                            e.currentTarget,
                                            tooltip
                                          );

                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);
                                        }
                                      }}
                                      onMouseLeave={() => {
                                        // Only hide if not in clicked mode
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (
                                          tooltip &&
                                          activeTooltip === tooltipId
                                        ) {
                                          // Use setTimeout to allow moving to the tooltip
                                          setTimeout(() => {
                                            // Only hide if we're not hovering over the tooltip itself
                                            if (
                                              activeTooltip === tooltipId &&
                                              !tooltip.matches(":hover") &&
                                              !tooltipTimeoutRef.current
                                            ) {
                                              tooltip.style.display = "none";
                                              setActiveTooltip(null);
                                            }
                                          }, 100);
                                        }
                                      }}
                                    >
                                      <Icon icon="solar:info-circle-bold" />
                                    </Button>

                                    {/* Other Amount Details Tooltip */}
                                    <div
                                      ref={(el) =>
                                      (tooltipRefs.current[
                                        `oth-amt-tooltip-${rowIndex}`
                                      ] = el)
                                      }
                                      className="shadow-lg rounded p-2 bg-white tooltip-container"
                                      style={{
                                        display: "none",
                                        width: "auto",
                                        maxWidth: "250px",
                                        zIndex: 9999,
                                        position: "fixed",
                                      }}
                                      onMouseEnter={() => {
                                        // Do nothing when hovering tooltip
                                        // This prevents the tooltip from disappearing
                                      }}
                                      onMouseLeave={(e) => {
                                        // Only hide if not in clicked mode
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        if (activeTooltip === tooltipId) {
                                          e.currentTarget.style.display =
                                            "none";
                                          setActiveTooltip(null);
                                        }
                                      }}
                                    >
                                      <div
                                        className="tooltip-arrow"
                                        style={{
                                          position: "absolute",
                                          width: "12px",
                                          height: "12px",
                                          backgroundColor: "white",
                                          border: "1px solid rgba(0,0,0,0.1)",
                                          transform: "rotate(45deg)",
                                          borderRight: "none",
                                          borderBottom: "none",
                                        }}
                                      ></div>
                                      <div
                                        className="other-amount-details"
                                        style={{
                                          maxHeight: "200px",
                                          overflowY: "auto",
                                        }}
                                      >
                                        {cell.otherAmountEntries.map(
                                          (entry, idx) => (
                                            <div
                                              key={idx}
                                              className="d-flex justify-content-between py-1"
                                              style={{
                                                borderBottom:
                                                  idx !==
                                                    cell.otherAmountEntries
                                                      .length -
                                                    1
                                                    ? "1px solid #eee"
                                                    : "none",
                                              }}
                                            >
                                              <span
                                                style={{
                                                  fontSize: "0.85rem",
                                                  flex: "1",
                                                  marginRight: "20px",
                                                }}
                                              >
                                                {entry.chargeType}
                                              </span>
                                              <span
                                                style={{
                                                  fontSize: "0.85rem",
                                                  minWidth: "80px",
                                                  textAlign: "right",
                                                }}
                                              >
                                                ₹
                                                {Number(entry.amount).toFixed(
                                                  2
                                                )}
                                              </span>
                                            </div>
                                          )
                                        )}
                                        <div className="mt-2 pt-2 border-top">
                                          <div className="d-flex justify-content-between">
                                            <strong className="text-dark">
                                              Total
                                            </strong>
                                            <strong className="text-dark">
                                              ₹
                                              {cell.otherAmountEntries
                                                .reduce(
                                                  (sum, entry) =>
                                                    sum + Number(entry.amount),
                                                  0
                                                )
                                                .toFixed(2)}
                                            </strong>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : getColumnType(colIndex) === "lsWt" ? (
                              <div className="d-flex align-items-center position-relative">
                                {(() => {
                                  const hasOtherAmountEntries =
                                    cell.otherAmountEntries?.length > 0;
                                  return (
                                    <input
                                      ref={(el) =>
                                      (inputRefs.current[
                                        `${rowIndex}-${colIndex}`
                                      ] = el)
                                      }
                                      type="text"
                                      value={cell.value}
                                      onChange={(e) => {
                                        // Only allow changes if there are no Other Amount entries
                                        if (!hasOtherAmountEntries) {
                                          const value = e.target.value;
                                          if (
                                            value === "" ||
                                            /^\d*\.?\d*$/.test(value)
                                          ) {
                                            handleInputChange(
                                              rowIndex,
                                              colIndex,
                                              value
                                            );
                                          }
                                        }
                                      }}
                                      className={`form-control border-0 ${buttonClass}`}
                                      style={{
                                        fontSize: "0.85rem",
                                        padding: "0.4rem 0.5rem",
                                        backgroundColor: "transparent",
                                        width: "100%",
                                        transition: "background-color 0.2s",
                                        cursor: hasOtherAmountEntries
                                          ? "not-allowed"
                                          : "text",
                                      }}
                                      onFocus={(e) => {
                                        if (!hasOtherAmountEntries) {
                                          handleFocus(rowIndex, colIndex);
                                        }
                                      }}
                                      onBlur={(e) => {
                                        e.target.style.backgroundColor =
                                          "transparent";
                                      }}
                                      readOnly={hasOtherAmountEntries}
                                      title={
                                        hasOtherAmountEntries
                                          ? "This value is calculated from Other Amount entries"
                                          : ""
                                      }
                                    />
                                  );
                                })()}
                              </div>
                            ) : getColumnType(colIndex) === "huidNo" ? (
                              <div className="d-flex align-items-center position-relative">
                                <input
                                  ref={(el) =>
                                  (inputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  readOnly
                                  onKeyDown={(e) => {
                                    handleKeyDown(
                                      e,
                                      rowIndex,
                                      colIndex,
                                      getColumnType(colIndex)
                                    );
                                    handleHuidKeyDown(e, rowIndex);
                                  }}
                                  onDoubleClick={(e) => {
                                    e.preventDefault();
                                    openHuidModal(rowIndex);
                                  }}
                                  className="form-control border-0 rounded-0"
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                    cursor: "pointer",
                                    caretColor: "transparent", // Hide the cursor
                                  }}
                                  onFocus={() =>
                                    handleFocus(rowIndex, colIndex)
                                  }
                                  onBlur={(e) => {
                                    e.target.style.backgroundColor =
                                      "transparent";
                                  }}
                                  title="Double-click or press Enter twice to edit HUID details"
                                />
                                <button
                                  className={`btn btn-link btn-sm p-0 ms-1 ${buttonClass}`}
                                  onClick={() => openHuidModal(rowIndex)}
                                  title="Click to edit HUID details"
                                  style={{ color: "var(--theme-primary)" }}
                                >
                                  <i className="bi bi-pencil-square"></i>
                                </button>
                                {cell.value && (
                                  <div className="position-relative">
                                    <button
                                      className={`btn btn-link btn-sm p-0 px-1 text-primary ${buttonClass}`}
                                      style={{ fontSize: "1rem", zIndex: 1 }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const tooltipId = `huid-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // If tooltip is already visible, hide it
                                          if (
                                            tooltip.style.display === "block"
                                          ) {
                                            tooltip.style.display = "none";
                                            setActiveTooltip(null);

                                            // Clear any existing timeout
                                            if (tooltipTimeoutRef.current) {
                                              clearTimeout(
                                                tooltipTimeoutRef.current
                                              );
                                              tooltipTimeoutRef.current = null;
                                            }
                                            return;
                                          }

                                          // Hide any other visible tooltips first
                                          Object.entries(
                                            tooltipRefs.current
                                          ).forEach(([id, t]) => {
                                            if (t && id !== tooltipId) {
                                              t.style.display = "none";
                                            }
                                          });

                                          // Position and show tooltip
                                          positionTooltip(
                                            e.currentTarget,
                                            tooltip
                                          );

                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);

                                          // Set timeout to hide after 4 seconds
                                          if (tooltipTimeoutRef.current) {
                                            clearTimeout(
                                              tooltipTimeoutRef.current
                                            );
                                          }

                                          tooltipTimeoutRef.current =
                                            setTimeout(() => {
                                              if (tooltip) {
                                                tooltip.style.display = "none";
                                                setActiveTooltip(null);
                                                tooltipTimeoutRef.current =
                                                  null;
                                              }
                                            }, 4000);
                                        }
                                      }}
                                      onMouseEnter={(e) => {
                                        // Don't show on hover if already showing from a click
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `huid-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // Hide any other visible tooltips first
                                          Object.entries(
                                            tooltipRefs.current
                                          ).forEach(([id, t]) => {
                                            if (t && id !== tooltipId) {
                                              t.style.display = "none";
                                            }
                                          });

                                          // Position and show tooltip
                                          positionTooltip(
                                            e.currentTarget,
                                            tooltip
                                          );

                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);
                                        }
                                      }}
                                      onMouseLeave={() => {
                                        // Only hide if not in clicked mode
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `huid-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (
                                          tooltip &&
                                          activeTooltip === tooltipId
                                        ) {
                                          // Use setTimeout to allow moving to the tooltip
                                          setTimeout(() => {
                                            // Only hide if we're not hovering over the tooltip itself
                                            if (
                                              activeTooltip === tooltipId &&
                                              !tooltip.matches(":hover") &&
                                              !tooltipTimeoutRef.current
                                            ) {
                                              tooltip.style.display = "none";
                                              setActiveTooltip(null);
                                            }
                                          }, 100);
                                        }
                                      }}
                                    // title="Click for HUID information"
                                    >
                                      <Icon icon="solar:info-circle-bold" />
                                    </button>
                                    {/* HUID Info Tooltip */}
                                    <div
                                      ref={(el) =>
                                      (tooltipRefs.current[
                                        `huid-tooltip-${rowIndex}`
                                      ] = el)
                                      }
                                      className="shadow-lg rounded p-2 bg-white tooltip-container"
                                      style={{
                                        display: "none",
                                        width: "auto",
                                        maxWidth: "250px",
                                        zIndex: 9999,
                                        position: "fixed",
                                      }}
                                      onMouseEnter={() => {
                                        // Do nothing when hovering tooltip
                                        // This prevents the tooltip from disappearing
                                      }}
                                      onMouseLeave={(e) => {
                                        // Only hide if not in clicked mode
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `huid-tooltip-${rowIndex}`;
                                        if (activeTooltip === tooltipId) {
                                          e.currentTarget.style.display =
                                            "none";
                                          setActiveTooltip(null);
                                        }
                                      }}
                                    >
                                      <div
                                        className="tooltip-arrow"
                                        style={{
                                          position: "absolute",
                                          width: "12px",
                                          height: "12px",
                                          backgroundColor: "white",
                                          border: "1px solid rgba(0,0,0,0.1)",
                                          transform: "rotate(45deg)",
                                          borderRight: "none",
                                          borderBottom: "none",
                                        }}
                                      ></div>
                                      <div
                                        className="huid-details"
                                        style={{
                                          maxHeight: "200px",
                                          overflowY: "auto",
                                        }}
                                      >
                                        {cell.value ? (
                                          cell.value
                                            .split(",")
                                            .map((huid, index, arr) => (
                                              <span
                                                key={index}
                                                style={{
                                                  fontSize: "0.85rem",
                                                  margin: 0,
                                                }}
                                              >
                                                {`${index + 1}. ${huid.trim()}`}
                                                {index < arr.length - 1 ? (
                                                  <br />
                                                ) : null}
                                              </span>
                                            ))
                                        ) : (
                                          <p
                                            style={{
                                              fontSize: "0.85rem",
                                              margin: 0,
                                            }}
                                          >
                                            No HUID entered
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <input
                                ref={(el) =>
                                (inputRefs.current[
                                  `${rowIndex}-${colIndex}`
                                ] = el)
                                }
                                type="text"
                                value={cell.value}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  const columnType = COLUMNS[colIndex].id;

                                  // Allow free text for certain fields
                                  if (columnType === "remarks") {
                                    handleInputChange(
                                      rowIndex,
                                      colIndex,
                                      value
                                    );
                                  }
                                  // For numeric fields only allow numbers and decimal
                                  else if (
                                    value === "" ||
                                    /^\d*\.?\d*$/.test(value)
                                  ) {
                                    handleInputChange(
                                      rowIndex,
                                      colIndex,
                                      value
                                    );
                                  }
                                }}
                                className="form-control border-0 rounded-0"
                                style={{
                                  fontSize: "0.85rem",
                                  padding: "0.4rem 0.5rem",
                                  backgroundColor: "transparent",
                                  width: "100%",
                                  transition: "background-color 0.2s",
                                  cursor: "text", // Change cursor to indicate it's editable
                                }}
                                onFocus={() => handleFocus(rowIndex, colIndex)}
                                onBlur={(e) => {
                                  e.target.style.backgroundColor =
                                    "transparent";
                                }}
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {/* Add Totals Row (only GWt, LWt, NWt) */}
                  <tr style={{ backgroundColor: "#f8f9fa" }}>
                    {COLUMNS.map((col, colIdx) => {
                      if (col.id === "no") {
                        return (
                          <td
                            key={col.id}
                            className="p-0"
                            style={{ width: col.width }}
                          >
                            <div
                              className="d-flex align-items-center justify-content-center"
                              style={{ height: "38px" }}
                            >
                              <span
                                style={{
                                  fontSize: "0.85rem",
                                  fontWeight: "600",
                                }}
                              >
                                Total
                              </span>
                            </div>
                          </td>
                        );
                      }
                      if (col.id === "grWt") {
                        const totalGWt = tableData
                          .reduce((sum, row) => {
                            const gwt = parseFloat(
                              row.data.find((cell) => cell.id === "grWt")
                                ?.value || 0
                            );
                            return sum + (isNaN(gwt) ? 0 : gwt);
                          }, 0)
                          .toFixed(2);
                        return (
                          <td
                            key={col.id}
                            className="p-0"
                            style={{ backgroundColor: "#fff" }}
                          >
                            <div
                              className="d-flex align-items-center justify-content-end px-2"
                              style={{ height: "38px" }}
                            >
                              <span
                                style={{
                                  fontSize: "0.85rem",
                                  fontWeight: "600",
                                }}
                              >
                                {totalGWt}
                              </span>
                            </div>
                          </td>
                        );
                      }
                      if (col.id === "lsWt") {
                        const totalLWt = tableData
                          .reduce((sum, row) => {
                            const lwt = parseFloat(
                              row.data.find((cell) => cell.id === "lsWt")
                                ?.value || 0
                            );
                            return sum + (isNaN(lwt) ? 0 : lwt);
                          }, 0)
                          .toFixed(2);
                        return (
                          <td
                            key={col.id}
                            className="p-0"
                            style={{ backgroundColor: "#fff" }}
                          >
                            <div
                              className="d-flex align-items-center justify-content-end px-2"
                              style={{ height: "38px" }}
                            >
                              <span
                                style={{
                                  fontSize: "0.85rem",
                                  fontWeight: "600",
                                }}
                              >
                                {totalLWt}
                              </span>
                            </div>
                          </td>
                        );
                      }
                      if (col.id === "netWt") {
                        const totalNWt = tableData
                          .reduce((sum, row) => {
                            const nwt = parseFloat(
                              row.data.find((cell) => cell.id === "netWt")
                                ?.value || 0
                            );
                            return sum + (isNaN(nwt) ? 0 : nwt);
                          }, 0)
                          .toFixed(2);
                        return (
                          <td
                            key={col.id}
                            className="p-0"
                            style={{ backgroundColor: "#fff" }}
                          >
                            <div
                              className="d-flex align-items-center justify-content-end px-2"
                              style={{ height: "38px" }}
                            >
                              <span
                                style={{
                                  fontSize: "0.85rem",
                                  fontWeight: "600",
                                }}
                              >
                                {totalNWt}
                              </span>
                            </div>
                          </td>
                        );
                      }
                      // All other columns in totals row stay blank
                      return <td key={col.id} className="p-0"></td>;
                    })}
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Visitor Fields Section - Below Table */}
            <div className="row mb-3 mt-3">
              {/* No of Visitors */}
              <div className="col-lg-6 col-md-6 col-sm-6">
                <div className="d-flex align-items-center mb-3">
                  <label
                    className="form-label me-2 mb-0 fw-semibold text-nowrap"
                    style={{ minWidth: "120px" }}
                  >
                    No of Visitors:
                  </label>
                  <input
                    type="number"
                    className={`form-control form-control-sm ${buttonClass}`}
                    value={noOfVisitors}
                    onChange={handleNoOfVisitorsChange}
                    min="1"
                    placeholder="Number"
                  />
                </div>

                <div className="d-flex align-items-center mb-3">
                  <label
                    className="form-label me-2 mb-0 fw-semibold text-nowrap"
                    style={{ minWidth: "120px" }}
                  >
                    Visitor Names:<span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control form-control-sm ${buttonClass}`}
                    value={visitorNames}
                    onChange={handleVisitorNamesChange}
                    placeholder="Enter visitor names"
                  />
                </div>

                <div className="d-flex align-items-center mb-3">
                  <label
                    className="form-label me-2 mb-0 fw-semibold text-nowrap"
                    style={{ minWidth: "120px" }}
                  >
                    Visitor Mobiles:
                  </label>
                  <input
                    type="text"
                    className={`form-control form-control-sm ${buttonClass}`}
                    value={visitorMobiles}
                    onChange={handleVisitorMobilesChange}
                    placeholder="Mobile No(s), comma separated"
                  />
                </div>

                <div className="d-flex align-items-center mb-3">
                  <label
                    className="form-label me-2 mb-0 fw-semibold text-nowrap"
                    style={{ minWidth: "120px", flexShrink: 0 }}
                  >
                    Reference Person:
                  </label>
                  <input
                    type="text"
                    className={`form-control form-control-sm ${buttonClass}`}
                    value={reference}
                    onChange={handleReferenceChange}
                    placeholder="Enter reference person"
                  />
                </div>

                <div className="d-flex align-items-start mb-3">
                  <label
                    className="form-label me-2 mb-0 fw-semibold text-nowrap"
                    style={{ minWidth: "120px", paddingTop: "6px" }}
                  >
                    Remarks:
                  </label>
                  <textarea
                    className={`form-control form-control-sm ${buttonClass}`}
                    value={remarks}
                    onChange={handleRemarksChange}
                    placeholder="Enter remarks"
                    maxLength="200"
                    rows="3"
                    style={{ resize: "vertical" }}
                  />
                </div>
              </div>
            </div>

            <div className="text-end mt-3">
              <button
                className={`btn btn-primary ${buttonClass}`}
                onClick={handleSubmit}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      </div>
      {renderDropdown()}
      {renderImageModal()}
      {renderHuidModal()}
      {renderMobileDropdown()}
    </div>
  );
};

export default AddJangad;
